import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import NavBar from '../components/NavBar/NavBar';
import LoadingAnimation from '../components/LoadingAnimation/LoadingAnimation';
import CoachAssessmentModal from '../components/CoachAssessmentModal/CoachAssessmentModal';
import { supabase } from '../supabaseClient';
import { apiFetch } from '../api/apiClient';
import exercisesData from '../assets/exercises.json';
import './coach.css';

const toNumOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_IMAGE_BUCKET || 'coach-images';
const EXERCISE_IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_EXERCISE_IMAGE_BUCKET || 'exercise-images';
const SIGNED_URL_TTL = 60 * 60;
const EXERCISE_UPLOAD_TIMEOUT_MS = 20000;
const DB_REQUEST_TIMEOUT_MS = 20000;

const formatDbError = (err, fallback = 'Database request failed') => {
  if (!err) return fallback;
  if (err instanceof Error && err.message) return err.message;
  const code = err.code ? `[${err.code}] ` : '';
  const message = err.message || fallback;
  const details = err.details ? ` Details: ${err.details}` : '';
  const hint = err.hint ? ` Hint: ${err.hint}` : '';
  return `${code}${message}${details}${hint}`.trim();
};

const runWithTimeout = async (label, operation, timeoutMs = DB_REQUEST_TIMEOUT_MS) => {
  let timeoutId;
  const timerPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`)), timeoutMs);
  });
  try {
    return await Promise.race([operation(), timerPromise]);
  } catch (error) {
    throw new Error(error?.message || label);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const cloneJson = (value) => {
  if (value == null) return null;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return null;
  }
};

const parseMealDayFromResponse = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return typeof value === 'object' ? value : null;
};

const isAbsoluteUrl = (value = '') => (
  value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')
);

const normalizeImages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          return isAbsoluteUrl(item) ? { url: item } : { path: item };
        }
        if (item && typeof item === 'object') {
          const direct = item.url || item.publicUrl || item.dataUrl || item.src || null;
          if (direct) return { url: direct };
          const path = item.path || item.storagePath || item.key || null;
          return path ? { path } : null;
        }
        return null;
      })
      .filter(Boolean);
  }
  return [];
};

const resolveSignedUrls = async (refs = []) => {
  if (!refs.length) return [];
  const urls = await Promise.all(refs.map(async (ref) => {
    if (ref.url) return ref.url;
    if (!ref.path) return null;
    try {
      const { data, error } = await supabase.storage
        .from(IMAGE_BUCKET)
        .createSignedUrl(ref.path, SIGNED_URL_TTL);
      if (error) return null;
      return data?.signedUrl || null;
    } catch {
      return null;
    }
  }));
  return urls.filter(Boolean);
};

const convertUnitToGrams = (value, unit) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  switch ((unit || '').toLowerCase()) {
    case 'oz':
    case 'ounce':
    case 'ounces':
      return num * 28.3495;
    case 'lb':
    case 'lbs':
    case 'pound':
    case 'pounds':
      return num * 453.592;
    case 'kg':
    case 'kilogram':
    case 'kilograms':
      return num * 1000;
    default:
      return null;
  }
};

const parseAmountToGrams = (amount) => {
  if (amount == null) return null;
  if (typeof amount === 'number') return Number.isFinite(amount) ? amount : null;
  const text = String(amount).trim();
  if (!text) return null;
  const gramMatch = text.match(/([\d.]+)\s*(?:g|grams?)/i);
  if (gramMatch) return Number(gramMatch[1]);
  const unitMatch = text.match(/([\d.]+)\s*(oz|ounce|ounces|lb|lbs|pound|pounds|kg|kilogram|kilograms)/i);
  if (unitMatch) {
    return convertUnitToGrams(unitMatch[1], unitMatch[2]);
  }
  const numericValue = Number(text);
  if (Number.isFinite(numericValue)) return numericValue;
  return null;
};

const normalizeAmountUnit = (value = '') => {
  const cleaned = String(value || '')
    .toLowerCase()
    .replace(/[^a-z\s-]/g, ' ')
    .trim();
  if (!cleaned) return '';
  if (/\begg(s)?\b/.test(cleaned)) return 'egg';
  const token = cleaned.split(/\s+/).find(Boolean) || '';
  if (!token) return '';
  if (token.endsWith('s') && token.length > 1) return token.slice(0, -1);
  return token;
};

const parseAmountWithUnit = (amount) => {
  if (amount == null) return null;
  if (typeof amount === 'number') {
    return Number.isFinite(amount) ? { value: amount, unit: '' } : null;
  }
  const text = String(amount).trim();
  if (!text) return null;
  const match = text.match(/^([\d.]+)\s*([a-zA-Z][a-zA-Z\s-]*)?$/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return {
    value,
    unit: normalizeAmountUnit(match[2] || ''),
  };
};

const numberOrZero = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : 0;
};

const roundedOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
};

const cloneAndRoundMacros = (macros = {}) => {
  if (!macros || typeof macros !== 'object') return {};
  const normalized = {
    protein_g: macros.protein_g ?? macros.protein,
    carbs_g: macros.carbs_g ?? macros.carbs,
    fats_g: macros.fats_g ?? macros.fat_g ?? macros.fat,
    calories: macros.calories ?? macros.kcal ?? macros.energy,
  };
  const rounded = {};
  Object.entries(normalized).forEach(([key, value]) => {
    if (value == null) return;
    const num = Number(value);
    if (Number.isFinite(num)) {
      rounded[key] = Math.round(num * 10) / 10;
    }
  });
  return rounded;
};

const ensureAmountLabel = (amount) => {
  if (!amount) return '-';
  const text = String(amount).trim();
  if (text === '-') return '-';
  if (!text) return '-';
  if (/g$/i.test(text)) return text;
  const numericValue = Number(text);
  if (Number.isFinite(numericValue)) {
    return `${numberOrZero(numericValue)}g`;
  }
  return `${text}g`;
};

const METADATA_LABELS = {
  category: 'Category',
  name: 'Food name',
  serving_size: 'Serving size',
  calories: 'Calories',
  protein_g: 'Protein',
  carbs_g: 'Carbs',
  fats_g: 'Fat',
  fat_g: 'Fat',
  fiber_g: 'Fiber',
  sodium_mg: 'Sodium',
  potassium_mg: 'Potassium',
  complex_carb: 'Complex carb',
  fatty_protein: 'Fatty protein',
  lean_protein: 'Lean protein',
  fruit: 'Fruit',
  green_veggie: 'Green veggie',
};

const METADATA_SORT_ORDER = [
  'category',
  'name',
  'serving_size',
  'calories',
  'protein_g',
  'carbs_g',
  'fats_g',
  'fat_g',
  'fiber_g',
  'sodium_mg',
  'potassium_mg',
  'complex_carb',
  'fatty_protein',
  'lean_protein',
  'fruit',
  'green_veggie',
  'sheet_name',
];

const formatMetadataValue = (key, value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  const numericValue =
    typeof value === 'number' || (typeof value === 'string' && value.trim())
      ? roundedOrNull(value)
      : null;
  if (numericValue !== null) {
    if (/_mg$/i.test(key)) return `${numericValue} mg`;
    if (/_g$/i.test(key)) return `${numericValue} g`;
    if (key === 'calories' || key === 'kcal' || key === 'energy') {
      return `${numericValue} kcal`;
    }
    return `${numericValue}`;
  }
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (key === 'category') {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }
  return trimmed;
};

const buildMetadataEntries = (metadata = null) => {
  if (!metadata || typeof metadata !== 'object') return [];
  const seen = new Set();
  const entries = Object.entries(metadata)
    .map(([key, value]) => {
      if (key === 'sheet_name') return null;
      if (seen.has(key)) return null;
      const formattedValue = formatMetadataValue(key, value);
      if (!formattedValue) return null;
      seen.add(key);
      return {
        key,
        label: METADATA_LABELS[key] || key.replace(/_/g, ' '),
        value: formattedValue,
      };
    })
    .filter(Boolean);
  return entries.sort((a, b) => {
    const aIdx = METADATA_SORT_ORDER.indexOf(a.key);
    const bIdx = METADATA_SORT_ORDER.indexOf(b.key);
    if (aIdx === -1 && bIdx === -1) return a.label.localeCompare(b.label);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });
};

const deriveNutritionFromMetadata = (metadata = null) => {
  if (!metadata || typeof metadata !== 'object') return null;
  const macros = {
    calories: metadata.calories ?? metadata.kcal ?? metadata.energy ?? null,
    protein_g: metadata.protein_g ?? metadata.protein ?? null,
    carbs_g: metadata.carbs_g ?? metadata.carbohydrates ?? metadata.carbs ?? null,
    fats_g: metadata.fats_g ?? metadata.fat_g ?? metadata.fat ?? null,
  };
  const normalized = {};
  Object.entries(macros).forEach(([key, value]) => {
    const normalizedValue = roundedOrNull(value);
    if (normalizedValue !== null) {
      normalized[key] = normalizedValue;
    }
  });
  return Object.keys(normalized).length ? normalized : null;
};

const normalizeNutrition = (macros = null, metadata = null) => {
  const derived = deriveNutritionFromMetadata(metadata);
  if (!macros && !derived) return null;
  const merged = { ...(derived || {}), ...(macros || {}) };
  const hasValue = Object.values(merged).some((val) => val !== null && val !== undefined);
  return hasValue ? merged : null;
};

const scaleMacrosForAmount = (macros = {}, servedAmount = null, baseAmount = null) => {
  const normalized = cloneAndRoundMacros(macros);
  const served = parseAmountToGrams(servedAmount);
  const base = parseAmountToGrams(baseAmount);
  let ratio = null;
  if (Number.isFinite(served) && Number.isFinite(base) && base > 0) {
    ratio = served / base;
  } else {
    const servedCount = parseAmountWithUnit(servedAmount);
    const baseCount = parseAmountWithUnit(baseAmount);
    if (
      servedCount
      && baseCount
      && baseCount.value > 0
      && servedCount.unit
      && baseCount.unit
      && servedCount.unit === baseCount.unit
    ) {
      ratio = servedCount.value / baseCount.value;
    }
  }
  if (!Number.isFinite(ratio) || ratio <= 0) {
    if (normalized.calories == null) {
      normalized.calories = Math.round(((normalized.protein_g || 0) * 4 + (normalized.carbs_g || 0) * 4 + (normalized.fats_g || 0) * 9) * 10) / 10;
    }
    return normalized;
  }
  const scaled = {};
  ['protein_g', 'carbs_g', 'fats_g', 'calories'].forEach((key) => {
    if (normalized[key] == null) return;
    scaled[key] = Math.round(normalized[key] * ratio * 10) / 10;
  });
  if (scaled.calories == null) {
    scaled.calories = Math.round(((scaled.protein_g || 0) * 4 + (scaled.carbs_g || 0) * 4 + (scaled.fats_g || 0) * 9) * 10) / 10;
  }
  return scaled;
};

const buildNutritionEntries = (macros = null) => {
  if (!macros) return [];
  return [
    macros.calories != null ? { key: 'calories', label: 'Calories', value: `${numberOrZero(macros.calories)} kcal` } : null,
    macros.protein_g != null ? { key: 'protein', label: 'Protein', value: `${numberOrZero(macros.protein_g)} g` } : null,
    macros.carbs_g != null ? { key: 'carbs', label: 'Carbs', value: `${numberOrZero(macros.carbs_g)} g` } : null,
    macros.fats_g != null ? { key: 'fats', label: 'Fats', value: `${numberOrZero(macros.fats_g)} g` } : null,
  ].filter(Boolean);
};

const computeFoodMacros = (food = {}) => {
  if (!food) return null;
  if (food.override_macros) {
    return cloneAndRoundMacros(food.override_macros);
  }
  const macros = food.nutrition_macros || food.nutrition_item?.macros || null;
  if (!macros) return null;
  const served = food.qty || food.amount || food.nutrition_amount || null;
  const base = food.nutrition_amount || food.nutrition_item?.amount || food.nutrition_metadata?.serving_size || null;
  return scaleMacrosForAmount(macros, served, base);
};

const sumFoodMacros = (foods = []) => {
  if (!Array.isArray(foods) || foods.length === 0) return null;
  const totals = {
    calories: 0,
    protein_g: 0,
    carbs_g: 0,
    fats_g: 0,
  };
  let hasAny = false;
  foods.forEach((food) => {
    const macros = computeFoodMacros(food);
    if (!macros) return;
    const calories = toNumOrNull(macros.calories);
    const protein = toNumOrNull(macros.protein_g);
    const carbs = toNumOrNull(macros.carbs_g);
    const fats = toNumOrNull(macros.fats_g);
    const roundedCalories = calories != null ? Math.round(calories) : null;
    const roundedProtein = protein != null ? Math.round(protein) : null;
    const roundedCarbs = carbs != null ? Math.round(carbs) : null;
    const roundedFats = fats != null ? Math.round(fats) : null;
    if (roundedCalories != null) {
      totals.calories += roundedCalories;
      hasAny = true;
    }
    if (roundedProtein != null) {
      totals.protein_g += roundedProtein;
      hasAny = true;
    }
    if (roundedCarbs != null) {
      totals.carbs_g += roundedCarbs;
      hasAny = true;
    }
    if (roundedFats != null) {
      totals.fats_g += roundedFats;
      hasAny = true;
    }
  });
  if (!hasAny) return null;
  if (!Number.isFinite(totals.calories) || totals.calories === 0) {
    const derived = deriveCaloriesFromMacros(totals.protein_g, totals.carbs_g, totals.fats_g);
    if (derived != null) {
      totals.calories = derived;
    }
  }
  return totals;
};

const formatMacroLine = (macros) => {
  if (!macros) return '';
  const proteinValue = macros.protein_g ?? macros.protein;
  const carbsValue = macros.carbs_g ?? macros.carbs;
  const fatsValue = macros.fats_g ?? macros.fat_g ?? macros.fat;
  const caloriesValue = macros.calories ?? macros.kcal ?? macros.energy;
  const protein = proteinValue != null ? Math.round(Number(proteinValue) || 0) : null;
  const carbs = carbsValue != null ? Math.round(Number(carbsValue) || 0) : null;
  const fats = fatsValue != null ? Math.round(Number(fatsValue) || 0) : null;
  let calories = caloriesValue != null ? Math.round(Number(caloriesValue) || 0) : null;
  if (calories == null && (protein != null || carbs != null || fats != null)) {
    calories = Math.round(((protein || 0) * 4 + (carbs || 0) * 4 + (fats || 0) * 9));
  }
  const parts = [];
  if (protein != null) parts.push(`P ${protein}g`);
  if (carbs != null) parts.push(`C ${carbs}g`);
  if (fats != null) parts.push(`F ${fats}g`);
  if (calories != null) parts.push(`${calories} kcal`);
  return parts.join(' / ');
};

const formatMacroValue = (value) => {
  if (value == null) return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return String(Math.round(num * 10) / 10);
};

const normalizeOptionKey = (value = '') => String(value || '').trim().toLowerCase();
const hasOptionNutrition = (map = {}, key = '') => Object.prototype.hasOwnProperty.call(map, key);
const hasMacros = (macros = {}) => {
  if (!macros || typeof macros !== 'object') return false;
  return Object.values(macros).some((val) => val !== null && val !== undefined && val !== '');
};
const normalizeMealMacros = (macros = null) => {
  if (!macros || typeof macros !== 'object') return null;
  const calories = toNumOrNull(macros.calories ?? macros.kcal ?? macros.energy ?? macros.total_kcal ?? macros.totalCalories);
  const protein = toNumOrNull(macros.protein_g ?? macros.protein);
  const carbs = toNumOrNull(macros.carbs_g ?? macros.carbs);
  const fats = toNumOrNull(macros.fats_g ?? macros.fat_g ?? macros.fat);
  const hasAny = calories != null || protein != null || carbs != null || fats != null;
  return hasAny ? {
    calories,
    protein_g: protein,
    carbs_g: carbs,
    fats_g: fats,
  } : null;
};
const deriveCaloriesFromMacros = (protein, carbs, fats) => {
  const p = toNumOrNull(protein);
  const c = toNumOrNull(carbs);
  const f = toNumOrNull(fats);
  if (p == null && c == null && f == null) return null;
  return Math.round(((p || 0) * 4 + (c || 0) * 4 + (f || 0) * 9));
};

const normalizeIngredientMeta = (value = {}) => {
  const cleaned = {};
  Object.entries(value).forEach(([key, val]) => {
    if (val === '' || val == null) return;
    cleaned[key] = val;
  });
  return cleaned;
};

const mapCategoryAlias = (value = '') => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '';
  if (key === 'protein') return 'protein';
  if (key === 'fat') return 'fat';
  if (key === 'fruit_veg' || key === 'produce' || key === 'vegetable' || key === 'vegetables' || key === 'fruit' || key === 'veg') return 'fruit_veg';
  if (key === 'carbohydrate' || key === 'carb' || key === 'carbs') return 'carb';
  return '';
};

const inferFoodCategoryFromSwapPayload = (swapPayload = {}, preferredCategory = '') => {
  const explicit = mapCategoryAlias(swapPayload?.category || preferredCategory);
  if (explicit) return explicit;

  const metadata = swapPayload?.metadata
    || swapPayload?.itemModel?.metadata
    || null;
  const metadataCategory = mapCategoryAlias(metadata?.category || '');
  if (metadataCategory) return metadataCategory;
  if (metadata?.fruit || metadata?.green_veggie || metadata?.vegetable || metadata?.vegetables) {
    return 'fruit_veg';
  }

  const itemName = String(swapPayload?.item || swapPayload?.replacement_name || '').trim().toLowerCase();
  if (itemName) {
    if (/(banana|apple|strawberr|blueberr|blackberr|raspberr|grape|orange|pineapple|mango|kiwi|melon|watermelon|fruit)/.test(itemName)) {
      return 'fruit_veg';
    }
    if (/honey|jam|jelly|syrup/.test(itemName)) {
      return 'carb';
    }
  }

  const macros = swapPayload?.macros || swapPayload?.itemModel?.macros || null;
  if (macros) {
    const protein = Number(macros.protein_g ?? macros.protein) || 0;
    const carbs = Number(macros.carbs_g ?? macros.carbs) || 0;
    const fats = Number(macros.fats_g ?? macros.fat_g ?? macros.fat) || 0;
    if (protein >= carbs && protein >= fats && protein > 0) return 'protein';
    if (carbs >= protein && carbs >= fats && carbs > 0) return 'carb';
    if (fats > 0) return 'fat';
  }

  return mapCategoryAlias(preferredCategory) || 'carb';
};

const buildIngredientContent = ({
  name,
  category,
  serving_size,
  calories,
  protein_g,
  carbs_g,
  fats_g,
  sodium_mg,
  potassium_mg,
  fiber_g,
}) => {
  const label = category ? `${category} source` : 'ingredient';
  const lines = [`${name}${label ? ` — ${label}.` : ''}`];
  if (serving_size) lines.push(`Serving Size: ${serving_size}`);
  if (calories != null) lines.push(`Calories: ${calories}`);
  if (protein_g != null) lines.push(`Protein: ${protein_g} g`);
  if (carbs_g != null) lines.push(`Carbs: ${carbs_g} g`);
  if (fats_g != null) lines.push(`Fat: ${fats_g} g`);
  if (fiber_g != null) lines.push(`Fiber: ${fiber_g} g`);
  if (sodium_mg != null) lines.push(`Sodium: ${sodium_mg} mg`);
  if (potassium_mg != null) lines.push(`Potassium: ${potassium_mg} mg`);
  return lines.join('\n');
};

const normalizeMealMacrosWithCalories = (macros = null) => {
  const normalized = normalizeMealMacros(macros);
  if (!normalized) return null;
  if (normalized.calories == null || normalized.calories === '') {
    const derived = deriveCaloriesFromMacros(normalized.protein_g, normalized.carbs_g, normalized.fats_g);
    if (derived != null) {
      normalized.calories = derived;
    }
  }
  return normalized;
};

const getMealPayloadNumber = (meal) => {
  const raw = meal?.meal_number ?? meal?.mealNumber ?? meal?.meal_num ?? null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

const getPayloadMealMacros = (payloadMeal) => (
  normalizeMealMacrosWithCalories(payloadMeal?.macros || payloadMeal?.total || payloadMeal?.totals || null)
);

const buildPayloadMealMacroMap = (payload) => {
  const map = new Map();
  const list = Array.isArray(payload?.meals) ? payload.meals : [];
  list.forEach((meal) => {
    const num = getMealPayloadNumber(meal);
    if (!Number.isFinite(num)) return;
    const macros = getPayloadMealMacros(meal);
    if (macros) {
      map.set(num, macros);
    }
  });
  return map;
};

const buildPayloadFoodMacroMap = (payload) => {
  const map = new Map();
  const list = Array.isArray(payload?.meals) ? payload.meals : [];
  list.forEach((meal) => {
    const num = getMealPayloadNumber(meal);
    if (!Number.isFinite(num)) return;
    const foods = Array.isArray(meal?.foods) ? meal.foods : [];
    const totals = sumFoodMacros(foods);
    if (totals) {
      map.set(num, totals);
    }
  });
  return map;
};

const sumMacroTotals = (macroMap) => {
  if (!macroMap || !macroMap.size) return null;
  const totals = {};
  let hasAny = false;
  macroMap.forEach((macros) => {
    if (!macros) return;
    const calories = toNumOrNull(macros.calories);
    const protein = toNumOrNull(macros.protein_g);
    const carbs = toNumOrNull(macros.carbs_g);
    const fats = toNumOrNull(macros.fats_g);
    if (calories != null) {
      totals.calories = (totals.calories || 0) + calories;
      hasAny = true;
    }
    if (protein != null) {
      totals.protein_g = (totals.protein_g || 0) + protein;
      hasAny = true;
    }
    if (carbs != null) {
      totals.carbs_g = (totals.carbs_g || 0) + carbs;
      hasAny = true;
    }
    if (fats != null) {
      totals.fats_g = (totals.fats_g || 0) + fats;
      hasAny = true;
    }
  });
  if (!hasAny) return null;
  if (totals.calories == null) {
    const derived = deriveCaloriesFromMacros(totals.protein_g, totals.carbs_g, totals.fats_g);
    if (derived != null) {
      totals.calories = derived;
    }
  }
  return totals;
};

const WORKOUT_DAY_BASE_LABELS = ['Rest', 'Cardio', 'Upper', 'Lower', 'Push', 'Pull', 'Legs'];
const WORKOUT_DAY_SUFFIXES = ['A', 'B', 'C', 'D', 'E', 'F'];
const WORKOUT_DAY_NAME_OPTIONS = [
  ...WORKOUT_DAY_BASE_LABELS.flatMap((base) => WORKOUT_DAY_SUFFIXES.map((suffix) => `${base} ${suffix}`)),
  'Full Body',
  'Upper Body',
  'Lower Body',
  'Arm Day',
  'Core',
  'Mobility',
  'Recovery',
];
const WORKOUT_DAY_TYPES = [
  { value: 'strength', label: 'Strength' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'recovery', label: 'Recovery' },
];
const INGREDIENT_CATEGORY_OPTIONS = [
  { value: 'protein', label: 'Protein' },
  { value: 'carb', label: 'Carb' },
  { value: 'fruit_veg', label: 'Fruit / Veg' },
  { value: 'fat', label: 'Fat' },
];
const inferWorkoutDisplayType = (name = '') => {
  const lower = String(name || '').toLowerCase();
  if (lower.includes('rest') || lower.includes('recovery')) return 'recovery';
  if (lower.includes('cardio')) return 'cardio';
  return 'strength';
};

const EXERCISE_FORCE_OPTIONS = ['pull', 'push', 'static'];
const EXERCISE_LEVEL_OPTIONS = ['beginner', 'intermediate', 'expert'];
const EXERCISE_MECHANIC_OPTIONS = ['compound', 'isolation'];
const EXERCISE_EQUIPMENT_OPTIONS = [
  'bands',
  'barbell',
  'body only',
  'cable',
  'dumbbell',
  'e-z curl bar',
  'exercise ball',
  'foam roll',
  'kettlebells',
  'machine',
  'medicine ball',
  'other',
];
const EXERCISE_CATEGORY_OPTIONS = [
  'cardio',
  'olympic weightlifting',
  'plyometrics',
  'powerlifting',
  'strength',
  'stretching',
  'strongman',
];
const EXERCISE_MUSCLE_OPTIONS = [
  'abdominals',
  'abductors',
  'adductors',
  'biceps',
  'calves',
  'chest',
  'forearms',
  'glutes',
  'hamstrings',
  'lats',
  'lower back',
  'middle back',
  'neck',
  'quadriceps',
  'shoulders',
  'traps',
  'triceps',
];

const makeExerciseId = (name = '') => (
  String(name || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const splitLines = (value = '') => (
  String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
);

const makeEmptyExerciseForm = () => ({
  name: '',
  force: '',
  level: '',
  mechanic: '',
  equipment: '',
  category: '',
  primaryMuscles: [],
  secondaryMuscles: [],
  instructions: '',
  images: [],
});

export default function CoachMemberPage() {
  const { memberId } = useParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [regeneratingWorkout, setRegeneratingWorkout] = useState(false);
  const [regeneratingMeals, setRegeneratingMeals] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);

  const [assessment, setAssessment] = useState(null);
  const [program, setProgram] = useState(null);
  const [workoutDays, setWorkoutDays] = useState([]);
  const [exercisesByDayId, setExercisesByDayId] = useState(new Map());
  const [exerciseDbList, setExerciseDbList] = useState([]);
  const [exerciseLibraryLoaded, setExerciseLibraryLoaded] = useState(false);
  const [exerciseLibraryLoading, setExerciseLibraryLoading] = useState(false);
  const [exerciseLibraryError, setExerciseLibraryError] = useState('');
  const [exercisePicker, setExercisePicker] = useState({ open: false, targetId: null });
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseFormOpen, setExerciseFormOpen] = useState(false);
  const [exerciseForm, setExerciseForm] = useState(() => makeEmptyExerciseForm());
  const [exerciseFormError, setExerciseFormError] = useState('');
  const [exerciseFormSaving, setExerciseFormSaving] = useState(false);
  const [exerciseUploadError, setExerciseUploadError] = useState('');
  const [exerciseUploading, setExerciseUploading] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseDetailsId, setExerciseDetailsId] = useState(null);
  const [mealDay, setMealDay] = useState(null);
  const [meals, setMeals] = useState([]);
  const [mealDayPayload, setMealDayPayload] = useState(null);
  const [weeklyCheckins, setWeeklyCheckins] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);

  const [editProgram, setEditProgram] = useState(false);
  const [editWorkouts, setEditWorkouts] = useState(false);
  const [savingWorkouts, setSavingWorkouts] = useState(false);
  const [workoutDraft, setWorkoutDraft] = useState({});
  const [workoutDayDraft, setWorkoutDayDraft] = useState({});
  const [editMealPlan, setEditMealPlan] = useState(false);
  const [savingMeals, setSavingMeals] = useState(false);
  const [mealDraft, setMealDraft] = useState({});
  const [mealFoodOpsDraft, setMealFoodOpsDraft] = useState([]);
  const mealPayloadEditBaseRef = useRef(null);
  const [swapModal, setSwapModal] = useState({ open: false, loading: false, error: '', options: [], context: null });
  const [swapForm, setSwapForm] = useState({
    item: '',
    amount: '',
    calories: '',
    protein_g: '',
    carbs_g: '',
    fats_g: '',
    itemModel: null,
  });
  const [optionNutritionMap, setOptionNutritionMap] = useState({});
  const [optionQuery, setOptionQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedNutrition, setSelectedNutrition] = useState(null);
  const [showOptionDetail, setShowOptionDetail] = useState(false);
  const [swapNotice, setSwapNotice] = useState('');
  const [optionCalcAmount, setOptionCalcAmount] = useState('');
  const [optionLoading, setOptionLoading] = useState(false);
  const [optionError, setOptionError] = useState('');
  const [ingredientCatalog, setIngredientCatalog] = useState([]);
  const [ingredientCatalogLoading, setIngredientCatalogLoading] = useState(false);
  const [ingredientCatalogError, setIngredientCatalogError] = useState('');
  const [ingredientCatalogLoaded, setIngredientCatalogLoaded] = useState(false);
  const [ingredientFormOpen, setIngredientFormOpen] = useState(false);
  const [ingredientFormSaving, setIngredientFormSaving] = useState(false);
  const [ingredientFormError, setIngredientFormError] = useState('');
  const [ingredientForm, setIngredientForm] = useState({
    name: '',
    serving_size: '',
    calories: '',
    protein_g: '',
    carbs_g: '',
    fats_g: '',
    sodium_mg: '',
    potassium_mg: '',
    fiber_g: '',
    category: 'protein',
    complex_carb: false,
    lean_protein: false,
    fatty_protein: false,
    fruit: false,
    green_veggie: false,
  });
  const [nutritionPrefs, setNutritionPrefs] = useState({ proteins: [], carbs: [], fats: [] });
  const optionRequestId = useRef(0);
  const optionPrefetchId = useRef(0);
  const ingredientRequestId = useRef(0);
  const swapNoticeTimeout = useRef(null);
  const messageTimeout = useRef(null);
  const swapModalBodyRef = useRef(null);
  const swapModalBottomRef = useRef(null);
  const [programForm, setProgramForm] = useState({
    calories: '',
    protein_g: '',
    carbs_g: '',
    fats_g: '',
    training_days: '',
    training_phase: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [{ data: aRow, error: aErr }, { data: pRow, error: pErr }] = await Promise.all([
        supabase
          .from('assessments')
          .select('user_id, name, age, gender, height_feet, height_inches, weight_lbs, assessment')
          .eq('user_id', memberId)
          .maybeSingle(),
        supabase
          .from('client_programs')
          .select('*')
          .eq('user_id', memberId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (aErr) throw aErr;
      if (pErr) throw pErr;
      setAssessment(aRow || null);
      setProgram(pRow || null);

      try {
        const { data: prefRow, error: prefErr } = await supabase
          .from('nutrition_preferences')
          .select('proteins, carbs, fats')
          .eq('user_id', memberId)
          .maybeSingle();
        if (prefErr) {
          setNutritionPrefs({ proteins: [], carbs: [], fats: [] });
        } else {
          setNutritionPrefs({
            proteins: prefRow?.proteins || [],
            carbs: prefRow?.carbs || [],
            fats: prefRow?.fats || [],
          });
        }
      } catch (_) {
        setNutritionPrefs({ proteins: [], carbs: [], fats: [] });
      }

      if (pRow?.id) {
        const programId = pRow.id;
        const { data: days, error: dErr } = await supabase
          .from('workout_days')
          .select('id, program_id, day_of_week, day_name, focus, duration_min, display_name, display_focus, display_duration_min, display_type, sequence, raw_payload, created_at')
          .eq('program_id', programId)
          .order('day_of_week', { ascending: true });
        if (dErr) throw dErr;
        setWorkoutDays(days || []);

        const { data: mpd, error: mpErr } = await supabase
          .from('meal_plan_days')
          .select('id, program_id, day_name, day_of_week, total_kcal, macros, raw_payload, created_at')
          .eq('program_id', programId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (mpErr) throw mpErr;
        setMealDay(mpd || null);
        if (mpd?.raw_payload) {
          let payload = mpd.raw_payload;
          if (typeof payload === 'string') {
            try {
              payload = JSON.parse(payload);
            } catch (_) {
              payload = null;
            }
          }
          setMealDayPayload(payload && typeof payload === 'object' ? payload : null);
        } else {
          setMealDayPayload(null);
        }

        // Metrics are optional: tolerate missing tables/policies.
        try {
          const { data: wci, error: wciErr } = await supabase
            .from('weekly_checkins')
            .select('id, createdat, week_start, weight, stress, sleep, energy_rating, training_rating, workouts_completed, notes, images')
            .eq('userid', memberId)
            .order('createdat', { ascending: false })
            .limit(8);
          if (wciErr || !Array.isArray(wci)) {
            setWeeklyCheckins([]);
          } else {
            const hydrated = await Promise.all(wci.map(async (row) => {
              const imageRefs = normalizeImages(row.images);
              const imageUrls = await resolveSignedUrls(imageRefs);
              return { ...row, imageRefs, imageUrls };
            }));
            setWeeklyCheckins(hydrated);
          }
        } catch (_) {
          setWeeklyCheckins([]);
        }

        try {
          const { data: dl, error: dlErr } = await supabase
            .from('daily_logs')
            .select('id, createdat, logdate, weight, steps, calories, notes')
            .eq('userid', memberId)
            .order('logdate', { ascending: false })
            .limit(14);
          setDailyLogs(dlErr ? [] : (dl || []));
        } catch (_) {
          setDailyLogs([]);
        }

        try {
          const { data: wsl, error: wslErr } = await supabase
            .from('workout_set_logs')
            .select('id, week_key, day_name, exercise_name, set_index, weight, reps, done, updated_at')
            .eq('user_id', memberId)
            .order('updated_at', { ascending: false })
            .limit(50);
          setWorkoutLogs(wslErr ? [] : (wsl || []));
        } catch (_) {
          setWorkoutLogs([]);
        }

        const dayIds = (days || []).map((d) => d.id).filter(Boolean);
        if (dayIds.length) {
          const { data: exRows, error: exErr } = await supabase
            .from('exercises')
            .select('id, day_id, ord, name, ex_type, sets, rep_min, rep_max, tempo, rir, rest_sec, created_at')
            .in('day_id', dayIds)
            .order('ord', { ascending: true });
          if (exErr) throw exErr;
          const map = new Map();
          (exRows || []).forEach((ex) => {
            if (!map.has(ex.day_id)) map.set(ex.day_id, []);
            map.get(ex.day_id).push(ex);
          });
          setExercisesByDayId(map);
        } else {
          setExercisesByDayId(new Map());
        }

        if (mpd?.id) {
          const { data: mealRows, error: mealErr } = await supabase
            .from('meals')
            .select('id, day_id, meal_number, name, time_window, macros, purpose, notes, created_at')
            .eq('day_id', mpd.id)
            .order('meal_number', { ascending: true });
          if (mealErr) throw mealErr;
          setMeals(mealRows || []);
        } else {
          setMeals([]);
        }
      } else {
        setWorkoutDays([]);
        setExercisesByDayId(new Map());
        setMealDay(null);
        setMeals([]);
        setMealDayPayload(null);
        setWeeklyCheckins([]);
        setDailyLogs([]);
        setWorkoutLogs([]);
      }
    } catch (e) {
      setError(e?.message || 'Unable to load member.');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!program) return;
    setProgramForm({
      calories: program.calories ?? '',
      protein_g: program.protein_g ?? '',
      carbs_g: program.carbs_g ?? '',
      fats_g: program.fats_g ?? '',
      training_days: program.training_days ?? '',
      training_phase: program.training_phase ?? '',
    });
  }, [program]);

  const memberName = assessment?.name || 'Member';
  const isRegenerating = regeneratingWorkout || regeneratingMeals;
  const regenerateStatusLabel = regeneratingWorkout
    ? 'Regenerating workout plan...'
    : regeneratingMeals
      ? 'Regenerating meal plan...'
      : '';
  const swapMode = swapModal.context?.mode || 'swap';
  const isAddMode = swapMode === 'add';

  const assessmentPayload = useMemo(() => {
    let parsed = assessment?.assessment || {};
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = {};
      }
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  }, [assessment]);

  const heightLabel = useMemo(() => {
    const ft = assessment?.height_feet ?? null;
    const inch = assessment?.height_inches ?? null;
    if (ft == null && inch == null) return null;
    const ftPart = ft != null ? `${ft}′` : '';
    const inchPart = inch != null ? `${inch}″` : '';
    return `${ftPart}${ftPart && inchPart ? ' ' : ''}${inchPart}`.trim() || null;
  }, [assessment]);

  const currentWeight = useMemo(() => {
    const w =
      dailyLogs?.[0]?.weight ??
      weeklyCheckins?.[0]?.weight ??
      assessment?.weight_lbs ??
      null;
    return w == null ? null : w;
  }, [assessment, dailyLogs, weeklyCheckins]);

  const primaryGoal = useMemo(() => {
    return (
      assessmentPayload?.goals?.primary ??
      assessmentPayload?.goals?.goal_primary ??
      assessmentPayload?.goals?.primary_goal ??
      assessmentPayload?.workout?.primary_goal ??
      null
    );
  }, [assessmentPayload]);

  const macroSummary = useMemo(() => {
    if (!program) return null;
    const payloadTotals = mealDayPayload
      ? sumMacroTotals(buildPayloadFoodMacroMap(mealDayPayload)) || sumMacroTotals(buildPayloadMealMacroMap(mealDayPayload))
      : null;
    const source = payloadTotals || program;
    const parts = [];
    if (source.calories != null) parts.push(`${Math.round(source.calories)} kcal`);
    const p = source.protein_g ?? null;
    const c = source.carbs_g ?? null;
    const f = source.fats_g ?? null;
    if (p != null || c != null || f != null) parts.push(`${Math.round(p || 0)}P / ${Math.round(c || 0)}C / ${Math.round(f || 0)}F`);
    return parts.join(' | ');
  }, [program, mealDayPayload]);

  const mealDayTotals = useMemo(() => {
    if (!mealDayPayload) return null;
    return sumMacroTotals(buildPayloadFoodMacroMap(mealDayPayload))
      || sumMacroTotals(buildPayloadMealMacroMap(mealDayPayload))
      || normalizeMealMacrosWithCalories(mealDayPayload?.total || mealDayPayload?.totals || mealDayPayload?.macros || null);
  }, [mealDayPayload]);

  const fallbackNutrition = useMemo(() => {
    const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
    const nutrition = assessmentPayload?.nutrition || assessmentPayload || {};
    return {
      proteins: toArray(nutrition.proteins || nutrition.protein),
      carbs: toArray(nutrition.carbs || nutrition.carb),
      fats: toArray(nutrition.fats || nutrition.fat),
    };
  }, [assessmentPayload]);

  const workoutPreferencesSummary = useMemo(() => {
    const workout = assessmentPayload?.workout && typeof assessmentPayload.workout === 'object'
      ? assessmentPayload.workout
      : {};
    const rawTrainingDays = workout.training_days ?? program?.training_days ?? null;
    const trainingDays = Number(rawTrainingDays);
    const trainingDaysLabel = Number.isFinite(trainingDays) ? `${trainingDays}` : 'Not set';

    const primaryGoalLabel = workout.primary_goal || primaryGoal || 'Not set';

    const splitLabel = workout?.preferred_split?.title
      || workout?.preferred_split?.code
      || 'Not set';

    const preferredDays = Array.isArray(workout.preferred_training_days)
      ? workout.preferred_training_days.filter(Boolean)
      : [];
    const preferredDaysLabel = preferredDays.length ? preferredDays.join(', ') : 'Not set';

    const focusAreas = Array.isArray(workout.focus_muscle_groups)
      ? workout.focus_muscle_groups.filter(Boolean)
      : [];
    const focusAreasLabel = !focusAreas.length || focusAreas.includes('ALL')
      ? 'All muscle groups'
      : focusAreas.join(', ');

    const limitations = Array.isArray(workout.limitations)
      ? workout.limitations.filter((item) => item && item !== 'None')
      : [];
    const limitationsLabel = limitations.length ? limitations.join(', ') : 'None';

    const experienceMap = {
      complete_beginner: 'Complete beginner',
      basic_familiarity: 'Basic familiarity',
      intermediate: 'Intermediate trainer',
      advanced: 'Advanced lifter',
    };
    const rawExperience = workout.experience_level || '';
    const experienceLabel = rawExperience
      ? (experienceMap[rawExperience] || String(rawExperience).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()))
      : 'Not set';

    return {
      trainingDaysLabel,
      primaryGoalLabel,
      splitLabel,
      preferredDaysLabel,
      focusAreasLabel,
      limitationsLabel,
      experienceLabel,
    };
  }, [assessmentPayload, program?.training_days, primaryGoal]);

  const mealPayloadByNumber = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(mealDayPayload?.meals) ? mealDayPayload.meals : [];
    list.forEach((m) => {
      const key = m?.meal_number ?? m?.mealNumber ?? null;
      if (key == null) return;
      map.set(Number(key), m);
    });
    return map;
  }, [mealDayPayload]);

  const addMealMacroLine = useMemo(() => {
    if (!isAddMode) return '';
    const num = Number(swapModal.context?.mealNumber);
    if (!Number.isFinite(num)) return '';
    const payloadMeal = mealPayloadByNumber.get(num) || null;
    const foods = Array.isArray(payloadMeal?.foods) ? payloadMeal.foods : [];
    const totals = sumFoodMacros(foods) || getPayloadMealMacros(payloadMeal) || null;
    return totals ? formatMacroLine(totals) : '';
  }, [isAddMode, swapModal.context?.mealNumber, mealPayloadByNumber]);

  const formatFoodCategoryLabel = useCallback((category) => {
    const key = String(category || '').toLowerCase();
    if (!key) return null;
    if (key === 'protein') return 'Protein';
    if (key === 'fat') return 'Fat';
    if (key === 'carb' || key === 'carbs') return 'Carb';
    if (key === 'fruit_veg' || key === 'produce' || key === 'vegetable' || key === 'vegetables') return 'Produce';
    return category;
  }, []);

  const getPreferenceOptions = useCallback((category) => {
    const key = String(category || '').toLowerCase();
    const pickList = (primary, fallback) => (Array.isArray(primary) && primary.length ? primary : (fallback || []));
    if (key === 'protein') return pickList(nutritionPrefs.proteins, fallbackNutrition.proteins);
    if (key === 'fat') return pickList(nutritionPrefs.fats, fallbackNutrition.fats);
    if (key === 'carb' || key === 'carbs' || key === 'fruit_veg' || key === 'produce') {
      return pickList(nutritionPrefs.carbs, fallbackNutrition.carbs);
    }
    return [];
  }, [nutritionPrefs, fallbackNutrition]);

  const getAccessToken = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token || '';
  }, []);

  const callBackend = useCallback(async (path, body, init = {}) => {
    const res = await apiFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
      body: JSON.stringify(body || {}),
      signal: init.signal,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.error || 'Request failed');
    }
    return json;
  }, []);

  const callBackendWithTimeout = useCallback(async (path, body, timeoutMs = 12000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await callBackend(path, body, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }, [callBackend]);

  const NUTRITION_LOOKUP_TIMEOUT_MS = 12000;
  const NUTRITION_PREFETCH_TIMEOUT_MS = 8000;
  const NUTRITION_LIST_TIMEOUT_MS = 60000;
  const MEAL_MUTATION_TIMEOUT_MS = 20000;

  const setSwapNoticeWithTimeout = useCallback((text) => {
    setSwapNotice(text);
    if (swapNoticeTimeout.current) {
      clearTimeout(swapNoticeTimeout.current);
    }
    swapNoticeTimeout.current = setTimeout(() => {
      setSwapNotice('');
    }, 2800);
  }, []);

  useEffect(() => {
    return () => {
      if (swapNoticeTimeout.current) {
        clearTimeout(swapNoticeTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!message) return;
    if (messageTimeout.current) {
      clearTimeout(messageTimeout.current);
    }
    messageTimeout.current = setTimeout(() => {
      setMessage('');
    }, 10000);
    return () => {
      if (messageTimeout.current) {
        clearTimeout(messageTimeout.current);
      }
    };
  }, [message]);

  useEffect(() => {
    if (!swapModal.open) return;
    const list = Array.isArray(swapModal.options) ? swapModal.options : [];
    const keys = list.map((item) => normalizeOptionKey(item)).filter(Boolean);
    const uniqueKeys = Array.from(new Set(keys));
    const missingKeys = uniqueKeys.filter((key) => !hasOptionNutrition(optionNutritionMap, key));
    if (!missingKeys.length) return;
    const requestId = ++optionPrefetchId.current;
    let cancelled = false;
    const preload = async () => {
      let accessToken = '';
      try {
        accessToken = await getAccessToken();
      } catch {
        accessToken = '';
      }
      const results = {};
      for (const key of missingKeys) {
        if (cancelled || requestId !== optionPrefetchId.current) return;
        const label = list.find((item) => normalizeOptionKey(item) === key) || key;
        try {
          const json = await callBackendWithTimeout('/nutrition/search', {
            query: label,
            accessToken,
            limit: 1,
            matchThreshold: 0.2,
          }, NUTRITION_PREFETCH_TIMEOUT_MS);
          const items = Array.isArray(json?.items) ? json.items : [];
          if (items.length) {
            results[key] = items[0];
          } else {
            results[key] = null;
          }
        } catch {
          // Ignore prefetch failures so we can retry on selection.
        }
      }
      if (!cancelled && requestId === optionPrefetchId.current && Object.keys(results).length) {
        setOptionNutritionMap((prev) => ({ ...prev, ...results }));
      }
    };
    preload();
    return () => {
      cancelled = true;
    };
  }, [swapModal.open, swapModal.options, optionNutritionMap, getAccessToken, callBackendWithTimeout, NUTRITION_PREFETCH_TIMEOUT_MS]);

  useEffect(() => {
    if (!swapModal.open || !isAddMode) return undefined;
    if (ingredientCatalogLoaded) return undefined;
    const requestId = ++ingredientRequestId.current;
    setIngredientCatalogLoading(true);
    setIngredientCatalogError('');
    (async () => {
      try {
        const accessToken = await getAccessToken();
        const json = await callBackendWithTimeout('/nutrition/list', {
          accessToken,
        }, NUTRITION_LIST_TIMEOUT_MS);
        if (requestId !== ingredientRequestId.current) return;
        const items = Array.isArray(json?.items) ? json.items : [];
        setIngredientCatalog(items);
        setIngredientCatalogLoaded(true);
        if (!items.length) {
          setIngredientCatalogError('No ingredients found.');
        }
      } catch (e) {
        if (requestId !== ingredientRequestId.current) return;
        const message = e?.name === 'AbortError'
          ? 'Ingredient list timed out. Try again.'
          : (e?.message || 'Unable to load ingredients.');
        setIngredientCatalogError(message);
      } finally {
        if (requestId === ingredientRequestId.current) {
          setIngredientCatalogLoading(false);
        }
      }
    })();
    return () => {
      if (requestId === ingredientRequestId.current) {
        ingredientRequestId.current += 1;
      }
    };
  }, [swapModal.open, isAddMode, ingredientCatalogLoaded, getAccessToken, callBackendWithTimeout, NUTRITION_LIST_TIMEOUT_MS]);

  const resetSwapModal = () => {
    if (swapNoticeTimeout.current) {
      clearTimeout(swapNoticeTimeout.current);
    }
    setSwapModal({ open: false, loading: false, error: '', options: [], context: null });
    setSwapForm({
      item: '',
      amount: '',
      calories: '',
      protein_g: '',
      carbs_g: '',
      fats_g: '',
      itemModel: null,
    });
    setOptionQuery('');
    setSelectedOption('');
    setSelectedNutrition(null);
    setShowOptionDetail(false);
    setSwapNotice('');
    setOptionCalcAmount('');
    setOptionLoading(false);
    setOptionError('');
    setIngredientFormOpen(false);
    setIngredientFormSaving(false);
    setIngredientFormError('');
    setIngredientForm({
      name: '',
      serving_size: '',
      calories: '',
      protein_g: '',
      carbs_g: '',
      fats_g: '',
      sodium_mg: '',
      potassium_mg: '',
      fiber_g: '',
      category: 'protein',
      complex_carb: false,
      lean_protein: false,
      fatty_protein: false,
      fruit: false,
      green_veggie: false,
    });
  };

  const openSwapModal = ({
    mealNumber,
    category,
    categoryIndex,
    currentLabel,
    currentAmount,
    currentMacros,
    mode = 'swap',
  }) => {
    if (swapNoticeTimeout.current) {
      clearTimeout(swapNoticeTimeout.current);
    }
    const isAdd = mode === 'add';
    const nextCategory = category || 'protein';
    const options = isAdd ? [] : Array.from(new Set(getPreferenceOptions(nextCategory)));
    const macros = currentMacros && typeof currentMacros === 'object' ? currentMacros : {};
    setSwapModal({
      open: true,
      loading: false,
      error: '',
      options,
      context: { mealNumber, category: isAdd ? (category || '') : nextCategory, categoryIndex, currentLabel, currentAmount, mode },
    });
    setSwapForm({
      item: currentLabel || '',
      amount: currentAmount || '',
      calories: macros.calories != null ? formatMacroValue(macros.calories) : '',
      protein_g: macros.protein_g != null ? formatMacroValue(macros.protein_g) : '',
      carbs_g: macros.carbs_g != null ? formatMacroValue(macros.carbs_g) : '',
      fats_g: macros.fats_g != null ? formatMacroValue(macros.fats_g) : '',
      itemModel: null,
    });
    setOptionQuery('');
    setSelectedOption('');
    setSelectedNutrition(null);
    setShowOptionDetail(false);
    setSwapNotice('');
    setOptionCalcAmount('');
    setOptionLoading(false);
    setOptionError('');
    setIngredientFormOpen(false);
    setIngredientFormSaving(false);
    setIngredientFormError('');
    setIngredientForm({
      name: '',
      serving_size: '',
      calories: '',
      protein_g: '',
      carbs_g: '',
      fats_g: '',
      sodium_mg: '',
      potassium_mg: '',
      fiber_g: '',
      category: 'protein',
      complex_carb: false,
      lean_protein: false,
      fatty_protein: false,
      fruit: false,
      green_veggie: false,
    });
  };

  const syncMealDayPayload = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    setMealDayPayload(payload);
    const foodMacroMap = buildPayloadFoodMacroMap(payload);
    const payloadMacroMap = buildPayloadMealMacroMap(payload);
    const macrosByMeal = foodMacroMap.size ? foodMacroMap : payloadMacroMap;
    if (macrosByMeal.size) {
      setMeals((prev) => (prev || []).map((meal) => {
        const num = Number(meal.meal_number);
        const macros = Number.isFinite(num) ? macrosByMeal.get(num) : null;
        return macros ? { ...meal, macros } : meal;
      }));
      if (editMealPlan) {
        setMealDraft((prev) => {
          const next = { ...prev };
          Object.values(prev).forEach((row) => {
            const num = Number(row.meal_number);
            if (!Number.isFinite(num)) return;
            const macros = macrosByMeal.get(num);
            if (!macros) return;
            next[row.id] = { ...row, macros };
          });
          return next;
        });
      }
    }
    const totals = sumMacroTotals(foodMacroMap)
      || normalizeMealMacrosWithCalories(payload?.total || payload?.totals || payload?.macros || null)
      || sumMacroTotals(payloadMacroMap);
    if (totals) {
      setMealDay((prev) => (prev ? { ...prev, total_kcal: totals?.calories ?? prev.total_kcal, macros: totals } : prev));
      setProgram((prev) => (prev ? {
        ...prev,
        calories: totals?.calories ?? prev.calories,
        protein_g: totals?.protein_g ?? prev.protein_g,
        carbs_g: totals?.carbs_g ?? prev.carbs_g,
        fats_g: totals?.fats_g ?? prev.fats_g,
      } : prev));
    }
  }, [editMealPlan]);

  const buildDraftFoodFromSwap = useCallback(({ existingFood = {}, ctx, swapPayload }) => {
    const food = { ...(existingFood || {}) };
    const mode = ctx?.mode || 'swap';
    const item = swapPayload?.item || '';
    const amount = swapPayload?.amount || swapPayload?.qty || null;
    const category = mode === 'add'
      ? inferFoodCategoryFromSwapPayload(swapPayload, ctx?.category || food.category)
      : (food.category || ctx?.category || 'protein');

    food.item = item;
    food.name = item;
    if (amount) {
      food.amount = amount;
      food.qty = amount;
    } else {
      delete food.amount;
      delete food.qty;
    }
    food.category = category;

    const macros = swapPayload?.macros && typeof swapPayload.macros === 'object'
      ? cloneAndRoundMacros(swapPayload.macros)
      : null;
    if (macros && Object.keys(macros).length > 0) {
      food.override_macros = macros;
    } else {
      delete food.override_macros;
    }

    const model = swapPayload?.itemModel && typeof swapPayload.itemModel === 'object'
      ? swapPayload.itemModel
      : null;
    if (model) {
      food.nutrition_item = {
        ...(food.nutrition_item && typeof food.nutrition_item === 'object' ? food.nutrition_item : {}),
        ...model,
      };
      if (model.metadata) {
        food.nutrition_metadata = model.metadata;
      }
      if (model.source) {
        food.source = model.source;
      }
    }
    if (swapPayload?.metadata && typeof swapPayload.metadata === 'object') {
      food.nutrition_metadata = swapPayload.metadata;
    }
    if (swapPayload?.source) {
      food.source = swapPayload.source;
    }
    return food;
  }, []);

  const stageSwapLocally = useCallback((ctx, swapPayload) => {
    const nextPayload = cloneJson(mealDayPayload);
    if (!nextPayload || !Array.isArray(nextPayload.meals)) return false;
    const mealNumber = Number(ctx?.mealNumber);
    if (!Number.isFinite(mealNumber)) return false;
    const mealIndex = nextPayload.meals.findIndex((meal) => Number(getMealPayloadNumber(meal)) === mealNumber);
    if (mealIndex < 0) return false;

    const meal = nextPayload.meals[mealIndex] && typeof nextPayload.meals[mealIndex] === 'object'
      ? { ...nextPayload.meals[mealIndex] }
      : {};
    const foods = Array.isArray(meal.foods) ? [...meal.foods] : [];
    const mode = ctx?.mode || 'swap';

    if (mode === 'add') {
      foods.push(buildDraftFoodFromSwap({ existingFood: {}, ctx, swapPayload }));
    } else {
      const wantedCategory = String(ctx?.category || '').toLowerCase();
      const wantedCategoryIndex = Number.isFinite(Number(ctx?.categoryIndex)) ? Number(ctx.categoryIndex) : 0;
      let targetIndex = -1;
      if (wantedCategory) {
        let seen = 0;
        for (let i = 0; i < foods.length; i += 1) {
          const rowCategory = String(foods[i]?.category || '').toLowerCase();
          if (rowCategory !== wantedCategory) continue;
          if (seen === wantedCategoryIndex) {
            targetIndex = i;
            break;
          }
          seen += 1;
        }
      }
      if (targetIndex < 0 && ctx?.currentLabel) {
        const label = String(ctx.currentLabel).trim().toLowerCase();
        targetIndex = foods.findIndex((row) => String(row?.item || row?.name || row?.food || row?.label || '').trim().toLowerCase() === label);
      }
      if (targetIndex < 0 && foods.length > 0) {
        targetIndex = 0;
      }
      if (targetIndex < 0) return false;
      foods[targetIndex] = buildDraftFoodFromSwap({ existingFood: foods[targetIndex], ctx, swapPayload });
    }

    meal.foods = foods;
    nextPayload.meals[mealIndex] = meal;
    syncMealDayPayload(nextPayload);
    return true;
  }, [mealDayPayload, buildDraftFoodFromSwap, syncMealDayPayload]);

  const stageRemoveIngredientLocally = useCallback(({
    mealNumber,
    foodIndex,
    category = null,
    categoryIndex = null,
    itemName = '',
  }) => {
    const nextPayload = cloneJson(mealDayPayload);
    if (!nextPayload || !Array.isArray(nextPayload.meals)) return false;
    const targetMeal = Number(mealNumber);
    if (!Number.isFinite(targetMeal)) return false;
    const targetIndex = Number(foodIndex);
    const wantedCategory = String(category || '').trim().toLowerCase();
    const wantedCategoryIndex = Number(categoryIndex);
    const wantedName = String(itemName || '').trim().toLowerCase();

    const mealIndex = nextPayload.meals.findIndex((meal) => Number(getMealPayloadNumber(meal)) === targetMeal);
    if (mealIndex < 0) return false;
    const meal = nextPayload.meals[mealIndex] && typeof nextPayload.meals[mealIndex] === 'object'
      ? { ...nextPayload.meals[mealIndex] }
      : {};
    const foods = Array.isArray(meal.foods) ? [...meal.foods] : [];
    let resolvedIndex = Number.isFinite(targetIndex) ? targetIndex : -1;
    if (resolvedIndex < 0 || resolvedIndex >= foods.length) {
      resolvedIndex = -1;
    }
    if (resolvedIndex < 0 && wantedCategory) {
      let seen = 0;
      for (let i = 0; i < foods.length; i += 1) {
        const rowCategory = String(foods[i]?.category || '').trim().toLowerCase();
        if (rowCategory !== wantedCategory) continue;
        if (!Number.isFinite(wantedCategoryIndex) || seen === wantedCategoryIndex) {
          resolvedIndex = i;
          break;
        }
        seen += 1;
      }
    }
    if (resolvedIndex < 0 && wantedName) {
      resolvedIndex = foods.findIndex((row) => (
        String(row?.item || row?.name || row?.food || row?.label || '')
          .trim()
          .toLowerCase() === wantedName
      ));
    }
    if (resolvedIndex < 0 || resolvedIndex >= foods.length) return false;
    foods.splice(resolvedIndex, 1);
    meal.foods = foods;
    nextPayload.meals[mealIndex] = meal;
    syncMealDayPayload(nextPayload);
    return true;
  }, [mealDayPayload, syncMealDayPayload]);

  const applySwap = async () => {
    const ctx = swapModal.context;
    if (!ctx) return;
    const mode = ctx.mode || 'swap';
    const item = swapForm.item.trim();
    if (!item) {
      setSwapModal((prev) => ({ ...prev, error: 'Choose a food item.' }));
      return;
    }
    const amount = swapForm.amount.trim();
    const macros = {};
    const calories = toNumOrNull(swapForm.calories);
    const protein = toNumOrNull(swapForm.protein_g);
    const carbs = toNumOrNull(swapForm.carbs_g);
    const fats = toNumOrNull(swapForm.fats_g);
    if (calories != null) macros.calories = calories;
    if (protein != null) macros.protein_g = protein;
    if (carbs != null) macros.carbs_g = carbs;
    if (fats != null) macros.fats_g = fats;
    const hasManualMacros = Object.keys(macros).length > 0;
    if (hasManualMacros && macros.calories == null) {
      macros.calories = Math.round(((macros.protein_g || 0) * 4 + (macros.carbs_g || 0) * 4 + (macros.fats_g || 0) * 9) * 10) / 10;
    }
    const swapPayload = {
      item,
      amount: amount || null,
      qty: amount || null,
    };
    if (hasManualMacros) {
      swapPayload.macros = macros;
    }
    if (swapForm.itemModel) {
      swapPayload.itemModel = swapForm.itemModel;
      if (swapForm.itemModel.metadata) swapPayload.metadata = swapForm.itemModel.metadata;
      if (swapForm.itemModel.source) swapPayload.source = swapForm.itemModel.source;
    }

    setSwapModal((prev) => ({ ...prev, loading: true, error: '' }));
    setError('');

    if (editMealPlan) {
      const staged = stageSwapLocally(ctx, swapPayload);
      if (!staged) {
        const fallbackMessage = mode === 'add'
          ? 'Unable to stage ingredient add.'
          : 'Unable to stage swap.';
        setSwapModal((prev) => ({ ...prev, loading: false, error: fallbackMessage }));
        return;
      }
      setMealFoodOpsDraft((prev) => ([
        ...prev,
        mode === 'add'
          ? {
            type: 'add',
            mealNumber: ctx.mealNumber,
            category: inferFoodCategoryFromSwapPayload(swapPayload, ctx?.category),
            food: swapPayload,
          }
          : {
            type: 'swap',
            mealNumber: ctx.mealNumber,
            category: ctx.category,
            categoryIndex: ctx.categoryIndex,
            swap: swapPayload,
          },
      ]));
      setMessage(mode === 'add'
        ? 'Ingredient added to draft. Click Save to persist.'
        : 'Swap added to draft. Click Save to persist.');
      setSwapModal((prev) => ({ ...prev, loading: false, error: '' }));
      resetSwapModal();
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      const accessToken = await getAccessToken();
      const json = mode === 'add'
        ? await callBackend('/meal-plan/add-food', {
          userId: memberId,
          accessToken,
          mealNumber: ctx.mealNumber,
          category: inferFoodCategoryFromSwapPayload(swapPayload, ctx?.category),
          food: swapPayload,
        })
        : await callBackend('/meal-plan/swap', {
          userId: memberId,
          accessToken,
          mealNumber: ctx.mealNumber,
          category: ctx.category,
          categoryIndex: ctx.categoryIndex,
          swap: swapPayload,
        });
      setMessage(mode === 'add' ? 'Ingredient added' : 'Meal updated');
      const payload = parseMealDayFromResponse(json?.meal_day);
      if (payload) {
        syncMealDayPayload(payload);
      }
      resetSwapModal();
    } catch (e) {
      const fallbackMessage = mode === 'add' ? 'Unable to add ingredient.' : 'Unable to apply swap.';
      setSwapModal((prev) => ({ ...prev, loading: false, error: e?.message || fallbackMessage }));
    } finally {
      setBusy(false);
    }
  };

  const filteredOptions = useMemo(() => {
    const list = Array.isArray(swapModal.options) ? swapModal.options : [];
    const query = optionQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((opt) => String(opt).toLowerCase().includes(query));
  }, [swapModal.options, optionQuery]);

  const optionMetadata = useMemo(() => (
    selectedNutrition?.metadata && typeof selectedNutrition.metadata === 'object'
      ? selectedNutrition.metadata
      : null
  ), [selectedNutrition]);

  const optionBaseAmount = useMemo(() => (
    selectedNutrition?.amount || optionMetadata?.serving_size || null
  ), [selectedNutrition, optionMetadata]);

  const optionBaseNutrition = useMemo(() => (
    selectedNutrition ? normalizeNutrition(selectedNutrition.macros || null, optionMetadata) : null
  ), [selectedNutrition, optionMetadata]);

  const baseOptionMacros = useMemo(() => (
    optionBaseNutrition ? cloneAndRoundMacros(optionBaseNutrition) : null
  ), [optionBaseNutrition]);

  const calcOptionMacros = useMemo(() => {
    if (!baseOptionMacros || !optionCalcAmount) return null;
    return scaleMacrosForAmount(baseOptionMacros, optionCalcAmount, optionBaseAmount);
  }, [baseOptionMacros, optionCalcAmount, optionBaseAmount]);

  const optionNutritionEntries = useMemo(() => {
    if (!optionBaseNutrition) return [];
    const macros = scaleMacrosForAmount(optionBaseNutrition, optionBaseAmount, optionBaseAmount);
    return buildNutritionEntries(macros);
  }, [optionBaseNutrition, optionBaseAmount]);

  const calcNutritionEntries = useMemo(() => {
    if (!calcOptionMacros) return [];
    return buildNutritionEntries(calcOptionMacros);
  }, [calcOptionMacros]);

  const optionMetadataEntries = useMemo(() => buildMetadataEntries(optionMetadata), [optionMetadata]);

  const fetchOptionNutrition = useCallback(async (itemName) => {
    const query = (itemName || '').trim();
    if (!query) return;
    const key = normalizeOptionKey(query);
    const requestId = ++optionRequestId.current;
    setOptionLoading(true);
    setOptionError('');
    setSelectedNutrition(null);
    try {
      const accessToken = await getAccessToken();
      const json = await callBackendWithTimeout('/nutrition/search', {
        query,
        accessToken,
        limit: 1,
        matchThreshold: 0.2,
      }, NUTRITION_LOOKUP_TIMEOUT_MS);
      if (requestId !== optionRequestId.current) return;
      const items = Array.isArray(json?.items) ? json.items : [];
      if (!items.length) {
        setOptionError('No nutrition data found for that item.');
        setSelectedNutrition(null);
        if (key) {
          setOptionNutritionMap((prev) => ({ ...prev, [key]: null }));
        }
        return;
      }
      setSelectedNutrition(items[0]);
      if (key) {
        setOptionNutritionMap((prev) => ({ ...prev, [key]: items[0] }));
      }
    } catch (e) {
      if (requestId !== optionRequestId.current) return;
      const message = e?.name === 'AbortError'
        ? 'Nutrition lookup timed out. Try again.'
        : (e?.message || 'Unable to load nutrition data.');
      setOptionError(message);
    } finally {
      if (requestId === optionRequestId.current) {
        setOptionLoading(false);
      }
    }
  }, [callBackendWithTimeout, getAccessToken, NUTRITION_LOOKUP_TIMEOUT_MS]);

  const getIngredientLabel = useCallback((opt) => {
    if (!opt) return '';
    return typeof opt === 'string'
      ? opt
      : (opt?.name || opt?.item || opt?.label || opt?.content || '');
  }, []);

  const filteredIngredients = useMemo(() => {
    const list = Array.isArray(ingredientCatalog) ? ingredientCatalog : [];
    const query = optionQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((opt) => getIngredientLabel(opt).toLowerCase().includes(query));
  }, [ingredientCatalog, optionQuery, getIngredientLabel]);

  const handleSelectOption = useCallback((opt) => {
    if (!opt) return;
    const itemLabel = getIngredientLabel(opt);
    if (!itemLabel) return;
    const key = normalizeOptionKey(itemLabel);
    setSelectedOption(itemLabel);
    setOptionCalcAmount('');
    setShowOptionDetail(true);
    setSwapNotice('');
    if (typeof opt === 'object') {
      setSelectedNutrition(opt);
      setOptionLoading(false);
      setOptionError('');
      return;
    }
    if (hasOptionNutrition(optionNutritionMap, key)) {
      const cached = optionNutritionMap[key] || null;
      setSelectedNutrition(cached);
      setOptionLoading(false);
      if (!cached) {
        setOptionError('No nutrition data found for that item.');
      } else {
        setOptionError('');
      }
      return;
    }
    setOptionError('');
    fetchOptionNutrition(opt);
  }, [fetchOptionNutrition, optionNutritionMap, getIngredientLabel]);

  const scrollSwapModalToBottom = useCallback(() => {
    const body = swapModalBodyRef.current;
    if (body) {
      body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
      return;
    }
    if (swapModalBottomRef.current) {
      swapModalBottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  useEffect(() => {
    if (!swapModal.open || !showOptionDetail) return undefined;
    const id = setTimeout(() => {
      scrollSwapModalToBottom();
    }, 0);
    return () => clearTimeout(id);
  }, [swapModal.open, showOptionDetail, scrollSwapModalToBottom]);

  const applyOptionToSwapForm = useCallback(() => {
    if (!selectedOption) return;
    const macros = calcOptionMacros || baseOptionMacros;
    setSwapForm((prev) => ({
      ...prev,
      item: selectedOption,
      amount: optionCalcAmount || optionBaseAmount || prev.amount,
      calories: macros?.calories != null ? formatMacroValue(macros.calories) : '',
      protein_g: macros?.protein_g != null ? formatMacroValue(macros.protein_g) : '',
      carbs_g: macros?.carbs_g != null ? formatMacroValue(macros.carbs_g) : '',
      fats_g: macros?.fats_g != null ? formatMacroValue(macros.fats_g) : '',
      itemModel: selectedNutrition ? {
        name: selectedNutrition?.name || selectedOption,
        amount: optionBaseAmount || null,
        macros: selectedNutrition?.macros || null,
        metadata: selectedNutrition?.metadata || null,
        source: selectedNutrition?.source || null,
        profile: selectedNutrition?.profile || null,
      } : prev.itemModel,
    }));
    setShowOptionDetail(false);
    setSwapNoticeWithTimeout('Macros added to manual swap fields. Click Apply swap to save.');
  }, [selectedOption, optionCalcAmount, optionBaseAmount, selectedNutrition, baseOptionMacros, calcOptionMacros, setSwapNoticeWithTimeout]);

  const removeIngredient = useCallback(async ({
    mealNumber,
    foodIndex,
    category = null,
    categoryIndex = null,
    itemName = '',
  }) => {
    if (mealNumber == null || foodIndex == null) return;
    if (editMealPlan) {
      const staged = stageRemoveIngredientLocally({
        mealNumber,
        foodIndex,
        category,
        categoryIndex,
        itemName,
      });
      if (!staged) {
        setError('Unable to stage ingredient removal.');
        return;
      }
      setMealFoodOpsDraft((prev) => ([
        ...prev,
        {
          type: 'remove',
          mealNumber,
          foodIndex,
          category,
          categoryIndex,
          itemName,
        },
      ]));
      setMessage('Ingredient removal staged. Click Save to persist.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const accessToken = await getAccessToken();
      const json = await callBackend('/meal-plan/remove-food', {
        userId: memberId,
        accessToken,
        mealNumber,
        foodIndex,
        category,
        categoryIndex,
        itemName,
      });
      setMessage('Ingredient removed');
      const payload = parseMealDayFromResponse(json?.meal_day);
      if (payload) {
        syncMealDayPayload(payload);
      }
    } catch (e) {
      setError(e?.message || 'Unable to remove ingredient.');
    } finally {
      setBusy(false);
    }
  }, [callBackend, editMealPlan, getAccessToken, memberId, stageRemoveIngredientLocally, syncMealDayPayload]);

  const saveNewIngredient = useCallback(async () => {
    const name = (ingredientForm.name || '').trim();
    if (!name) {
      setIngredientFormError('Ingredient name is required.');
      return;
    }
    const hasTag = !!(
      ingredientForm.complex_carb
      || ingredientForm.lean_protein
      || ingredientForm.fatty_protein
      || ingredientForm.fruit
      || ingredientForm.green_veggie
    );
    if (!hasTag) {
      setIngredientFormError('Select at least one tag.');
      return;
    }
    const calories = toNumOrNull(ingredientForm.calories);
    const protein = toNumOrNull(ingredientForm.protein_g);
    const carbs = toNumOrNull(ingredientForm.carbs_g);
    const fats = toNumOrNull(ingredientForm.fats_g);
    if (calories == null && protein == null && carbs == null && fats == null) {
      setIngredientFormError('Add at least one macro value (calories, protein, carbs, or fats).');
      return;
    }
    setIngredientFormSaving(true);
    setIngredientFormError('');
    try {
      const accessToken = await getAccessToken();
      const meta = normalizeIngredientMeta({
        name,
        serving_size: ingredientForm.serving_size?.trim() || null,
        calories: calories ?? null,
        protein_g: protein ?? null,
        carbs_g: carbs ?? null,
        fats_g: fats ?? null,
        fat_g: fats ?? null,
        sodium_mg: toNumOrNull(ingredientForm.sodium_mg),
        potassium_mg: toNumOrNull(ingredientForm.potassium_mg),
        fiber_g: toNumOrNull(ingredientForm.fiber_g),
        category: ingredientForm.category || null,
        complex_carb: !!ingredientForm.complex_carb,
        lean_protein: !!ingredientForm.lean_protein,
        fatty_protein: !!ingredientForm.fatty_protein,
        fruit: !!ingredientForm.fruit,
        green_veggie: !!ingredientForm.green_veggie,
        sheet_name: 'Coach Added',
      });
      const content = buildIngredientContent({
        name,
        category: meta.category,
        serving_size: meta.serving_size,
        calories: meta.calories,
        protein_g: meta.protein_g,
        carbs_g: meta.carbs_g,
        fats_g: meta.fats_g ?? meta.fat_g,
        sodium_mg: meta.sodium_mg,
        potassium_mg: meta.potassium_mg,
        fiber_g: meta.fiber_g,
      });
      await callBackend('/nutrition/add', {
        accessToken,
        item: {
          name,
          content,
          metadata: meta,
        },
      });
      const newItem = {
        name,
        amount: meta.serving_size || null,
        details: content,
        macros: {
          calories: meta.calories ?? null,
          protein_g: meta.protein_g ?? null,
          carbs_g: meta.carbs_g ?? null,
          fats_g: meta.fats_g ?? meta.fat_g ?? null,
        },
        metadata: meta,
        source: 'coach',
      };
      setIngredientCatalog((prev) => [newItem, ...(prev || [])]);
      setIngredientCatalogLoaded(true);
      setIngredientFormOpen(false);
      setIngredientForm({
        name: '',
        serving_size: '',
        calories: '',
        protein_g: '',
        carbs_g: '',
        fats_g: '',
        sodium_mg: '',
        potassium_mg: '',
        fiber_g: '',
        category: 'protein',
        complex_carb: false,
        lean_protein: false,
        fatty_protein: false,
        fruit: false,
        green_veggie: false,
      });
      setOptionQuery(name);
      handleSelectOption(newItem);
    } catch (e) {
      setIngredientFormError(e?.message || 'Unable to save ingredient.');
    } finally {
      setIngredientFormSaving(false);
    }
  }, [ingredientForm, getAccessToken, callBackend, handleSelectOption]);

  const cancelProgramEdit = () => {
    if (!program) return;
    setProgramForm({
      calories: program.calories ?? '',
      protein_g: program.protein_g ?? '',
      carbs_g: program.carbs_g ?? '',
      fats_g: program.fats_g ?? '',
      training_days: program.training_days ?? '',
      training_phase: program.training_phase ?? '',
    });
    setEditProgram(false);
  };

  const saveProgram = async () => {
    if (!program?.id) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        calories: toNumOrNull(programForm.calories),
        protein_g: toNumOrNull(programForm.protein_g),
        carbs_g: toNumOrNull(programForm.carbs_g),
        fats_g: toNumOrNull(programForm.fats_g),
        training_days: toNumOrNull(programForm.training_days),
        training_phase: programForm.training_phase || null,
      };
      const { error: uErr } = await supabase.from('client_programs').update(payload).eq('id', program.id);
      if (uErr) throw uErr;
      setProgram((prev) => (prev ? { ...prev, ...payload } : prev));
      setMessage('Program updated');
      setEditProgram(false);
    } catch (e) {
      setError(e?.message || 'Unable to update program.');
    } finally {
      setBusy(false);
    }
  };

  const createBlankProgram = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        user_id: memberId,
        goal: 'General',
        calories: null,
        protein_g: null,
        carbs_g: null,
        fats_g: null,
        training_days: 3,
        cycle_weeks: 4,
        active: true,
      };
      const { error: insErr } = await supabase.from('client_programs').insert(payload);
      if (insErr) throw insErr;
      setMessage('Program created');
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to create program.');
    } finally {
      setBusy(false);
    }
  };

  const regenerateWorkout = async () => {
    setRegeneratingWorkout(true);
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const accessToken = await getAccessToken();
      await callBackend('/generate-workout-plan', {
        userId: memberId,
        accessToken,
        trainingDays: program?.training_days ?? undefined,
      });
      setMessage('Workout plan regenerated');
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to regenerate workout plan.');
    } finally {
      setBusy(false);
      setRegeneratingWorkout(false);
    }
  };

  const regenerateMeals = async () => {
    setRegeneratingMeals(true);
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const accessToken = await getAccessToken();
      const macroTargets = {
        calories: toNumOrNull(programForm.calories),
        protein_g: toNumOrNull(programForm.protein_g),
        carbs_g: toNumOrNull(programForm.carbs_g),
        fats_g: toNumOrNull(programForm.fats_g),
      };
      const hasMacroTargets = Object.values(macroTargets).some((value) => value != null);
      await callBackend('/generate-meal-plan', {
        userId: memberId,
        accessToken,
        ...(hasMacroTargets ? { macroTargets } : {}),
      });
      setMessage('Meal plan regenerated');
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to regenerate meal plan.');
    } finally {
      setBusy(false);
      setRegeneratingMeals(false);
    }
  };

  const resetMeals = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const accessToken = await getAccessToken();
      await callBackend('/reset-meal-plan', { userId: memberId, accessToken });
      setMessage('Meal plan cleared');
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to clear meal plan.');
    } finally {
      setBusy(false);
    }
  };

  const resetWorkoutPlan = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const accessToken = await getAccessToken();
      await callBackend('/reset-workout-plan', { userId: memberId, accessToken });
      setMessage('Workout plan cleared');
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to clear workout plan.');
    } finally {
      setBusy(false);
    }
  };

  const makeTmpId = () => `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const startEditWorkouts = () => {
    const next = {};
    const nextDays = {};
    exercisesByDayId.forEach((list) => {
      (list || []).forEach((ex) => {
        next[ex.id] = { ...ex, __new: false, __deleted: false };
      });
    });
    (workoutDays || []).forEach((day) => {
      const displaySeed = day.display_name ?? day.day_name ?? '';
      const displayIsPreset = WORKOUT_DAY_NAME_OPTIONS.includes(displaySeed);
      nextDays[day.id] = {
        id: day.id,
        program_id: day.program_id ?? null,
        display_name: displaySeed,
        display_name_mode: displayIsPreset ? 'preset' : 'custom',
        display_focus: day.display_focus ?? day.focus ?? '',
        display_duration_min: day.display_duration_min ?? day.duration_min ?? '',
        display_type: day.display_type ?? inferWorkoutDisplayType(day.day_name),
        notes: (day.raw_payload && typeof day.raw_payload === 'object' ? day.raw_payload.notes : '') ?? '',
        raw_payload: day.raw_payload && typeof day.raw_payload === 'object' ? { ...day.raw_payload } : {},
      };
    });
    setWorkoutDraft(next);
    setWorkoutDayDraft(nextDays);
    setEditWorkouts(true);
  };

  const cancelEditWorkouts = () => {
    setEditWorkouts(false);
    setWorkoutDraft({});
    setWorkoutDayDraft({});
  };

  const patchWorkoutDraft = (exerciseId, patch) => {
    setWorkoutDraft((prev) => ({
      ...prev,
      [exerciseId]: { ...(prev[exerciseId] || {}), ...patch },
    }));
  };

  const patchWorkoutDayDraft = (dayId, patch) => {
    setWorkoutDayDraft((prev) => ({
      ...prev,
      [dayId]: { ...(prev[dayId] || {}), ...patch },
    }));
  };

  const exerciseLibrary = useMemo(() => {
    const map = new Map();
    (exercisesData || []).forEach((ex) => {
      if (!ex) return;
      const key = ex.id || ex.name;
      if (!key) return;
      map.set(key, { ...ex, source: 'json' });
    });
    (exerciseDbList || []).forEach((ex) => {
      if (!ex) return;
      const key = ex.id || ex.name;
      if (!key) return;
      map.set(key, { ...ex, source: 'db' });
    });
    return Array.from(map.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [exerciseDbList]);

  const filteredExerciseLibrary = useMemo(() => {
    const query = exerciseSearch.trim().toLowerCase();
    if (!query) return exerciseLibrary;
    return exerciseLibrary.filter((ex) => {
      const haystack = [
        ex.name,
        ex.id,
        ex.category,
        ex.equipment,
        ex.force,
        ex.level,
        ex.mechanic,
        ...(ex.primaryMuscles || []),
        ...(ex.secondaryMuscles || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [exerciseLibrary, exerciseSearch]);

  const resetExerciseForm = useCallback(() => {
    setExerciseForm(makeEmptyExerciseForm());
    setExerciseFormError('');
    setExerciseFormSaving(false);
    setExerciseUploadError('');
    setExerciseUploading(false);
  }, []);

  const selectedExerciseName = exercisePicker.targetId
    ? workoutDraft[exercisePicker.targetId]?.name
    : '';

  useEffect(() => {
    if (!exercisePicker.open) return;
    if (selectedExercise || !selectedExerciseName || !exerciseLibrary.length) return;
    const match = exerciseLibrary.find((ex) => ex.name === selectedExerciseName);
    if (match) setSelectedExercise(match);
  }, [exercisePicker.open, selectedExercise, selectedExerciseName, exerciseLibrary]);

  const loadExerciseLibrary = useCallback(async () => {
    if (exerciseLibraryLoading) return;
    setExerciseLibraryLoading(true);
    setExerciseLibraryError('');
    try {
      const { data, error } = await supabase
        .from('exercise_list')
        .select('id, name, force, level, mechanic, equipment, primary_muscles, secondary_muscles, instructions, category, images')
        .order('name', { ascending: true });
      if (error) throw error;
      const normalized = (data || []).map((row) => ({
        id: row.id,
        name: row.name,
        force: row.force,
        level: row.level,
        mechanic: row.mechanic,
        equipment: row.equipment,
        primaryMuscles: Array.isArray(row.primary_muscles) ? row.primary_muscles : [],
        secondaryMuscles: Array.isArray(row.secondary_muscles) ? row.secondary_muscles : [],
        instructions: Array.isArray(row.instructions) ? row.instructions : [],
        category: row.category,
        images: Array.isArray(row.images) ? row.images : [],
        source: 'db',
      }));
      setExerciseDbList(normalized);
      setExerciseLibraryLoaded(true);
    } catch (e) {
      setExerciseLibraryError(e?.message || 'Unable to load exercise library.');
    } finally {
      setExerciseLibraryLoading(false);
    }
  }, [exerciseLibraryLoading]);

  useEffect(() => {
    if (!exercisePicker.open) return;
    if (exerciseLibraryLoaded || exerciseLibraryLoading) return;
    loadExerciseLibrary();
  }, [exercisePicker.open, exerciseLibraryLoaded, exerciseLibraryLoading, loadExerciseLibrary]);

  const openExercisePicker = useCallback((exerciseId) => {
    setExercisePicker({ open: true, targetId: exerciseId });
    setExerciseSearch('');
    setExerciseFormOpen(false);
    setExerciseFormError('');
    setExerciseLibraryError('');
    setSelectedExercise(null);
    setExerciseDetailsId(null);
    if (!exerciseLibraryLoaded && !exerciseLibraryLoading) {
      loadExerciseLibrary();
    }
  }, [exerciseLibraryLoaded, exerciseLibraryLoading, loadExerciseLibrary]);

  const closeExercisePicker = useCallback(() => {
    setExercisePicker({ open: false, targetId: null });
    setExerciseSearch('');
    setExerciseFormOpen(false);
    setExerciseFormError('');
    setExerciseFormSaving(false);
    setSelectedExercise(null);
    setExerciseDetailsId(null);
  }, []);

  const toggleExerciseMuscle = useCallback((field, value) => {
    setExerciseForm((prev) => {
      const list = Array.isArray(prev[field]) ? prev[field] : [];
      const set = new Set(list);
      if (set.has(value)) {
        set.delete(value);
      } else {
        set.add(value);
      }
      return { ...prev, [field]: Array.from(set) };
    });
  }, []);

  const applyExerciseSelection = useCallback((exercise) => {
    if (!exercisePicker.targetId) return;
    patchWorkoutDraft(exercisePicker.targetId, { name: exercise?.name || '' });
    closeExercisePicker();
  }, [exercisePicker.targetId, patchWorkoutDraft, closeExercisePicker]);

  const saveExerciseForm = async () => {
    if (exerciseFormSaving) return;
    const name = exerciseForm.name.trim();
    const id = makeExerciseId(name);
    const instructions = splitLines(exerciseForm.instructions);
    const images = Array.isArray(exerciseForm.images) ? exerciseForm.images : [];
    const errors = [];

    if (!name) errors.push('Name is required.');
    if (!id) errors.push('Id is required.');
    if (!exerciseForm.force) errors.push('Force is required.');
    if (!exerciseForm.level) errors.push('Level is required.');
    if (!exerciseForm.mechanic) errors.push('Mechanic is required.');
    if (!exerciseForm.equipment) errors.push('Equipment is required.');
    if (!exerciseForm.category) errors.push('Category is required.');
    if (!exerciseForm.primaryMuscles.length) errors.push('Select at least one primary muscle.');
    if (!instructions.length) errors.push('Add at least one instruction line.');
    if (!images.length) errors.push('Upload at least one image.');

    if (errors.length) {
      setExerciseFormError(errors[0]);
      return;
    }

    setExerciseFormSaving(true);
    setExerciseFormError('');
    try {
      const payload = {
        id,
        name,
        force: exerciseForm.force,
        level: exerciseForm.level,
        mechanic: exerciseForm.mechanic,
        equipment: exerciseForm.equipment,
        category: exerciseForm.category,
        primary_muscles: exerciseForm.primaryMuscles,
        secondary_muscles: exerciseForm.secondaryMuscles || [],
        instructions,
        images,
      };
      const { data, error } = await supabase
        .from('exercise_list')
        .insert(payload)
        .select('id, name, force, level, mechanic, equipment, primary_muscles, secondary_muscles, instructions, category, images')
        .single();
      if (error) throw error;
      const normalized = {
        id: data.id,
        name: data.name,
        force: data.force,
        level: data.level,
        mechanic: data.mechanic,
        equipment: data.equipment,
        primaryMuscles: Array.isArray(data.primary_muscles) ? data.primary_muscles : [],
        secondaryMuscles: Array.isArray(data.secondary_muscles) ? data.secondary_muscles : [],
        instructions: Array.isArray(data.instructions) ? data.instructions : [],
        category: data.category,
        images: Array.isArray(data.images) ? data.images : [],
        source: 'db',
      };
      setExerciseDbList((prev) => {
        const next = [...(prev || [])];
        const idx = next.findIndex((row) => row.id === normalized.id);
        if (idx >= 0) {
          next[idx] = normalized;
        } else {
          next.push(normalized);
        }
        next.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        return next;
      });
      setExerciseLibraryLoaded(true);
      if (exercisePicker.targetId) {
        patchWorkoutDraft(exercisePicker.targetId, { name: normalized.name });
      }
      closeExercisePicker();
      resetExerciseForm();
    } catch (e) {
      setExerciseFormError(e?.message || 'Unable to save exercise.');
    } finally {
      setExerciseFormSaving(false);
    }
  };

  const uploadExerciseImages = async (files = []) => {
    if (!files.length || exerciseUploading) return;
    setExerciseUploading(true);
    setExerciseUploadError('');
    const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Upload timed out. Please try again.')), ms);
      promise
        .then((value) => {
          clearTimeout(timer);
          resolve(value);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });
    try {
      const baseId = makeExerciseId(exerciseForm.name) || 'exercise';
      const uploadedPaths = [];
      for (const file of files) {
        const safeName = file.name.replace(/[^a-zA-Z0-9_.-]+/g, '_');
        const path = `${baseId}/${Date.now()}_${safeName}`;
        const uploadPromise = supabase.storage
          .from(EXERCISE_IMAGE_BUCKET)
          .upload(path, file);
        const { error } = await withTimeout(uploadPromise, EXERCISE_UPLOAD_TIMEOUT_MS);
        if (error) {
          throw error;
        }
        uploadedPaths.push(path);
      }
      setExerciseForm((prev) => ({
        ...prev,
        images: [...(Array.isArray(prev.images) ? prev.images : []), ...uploadedPaths],
      }));
    } catch (e) {
      setExerciseUploadError(e?.message || 'Unable to upload images.');
    } finally {
      setExerciseUploading(false);
    }
  };

  const removeExerciseImage = async (path) => {
    if (!path) return;
    try {
      await supabase.storage.from(EXERCISE_IMAGE_BUCKET).remove([path]);
    } catch (_) {
      // Ignore delete failures; still remove from form list.
    }
    setExerciseForm((prev) => ({
      ...prev,
      images: (prev.images || []).filter((img) => img !== path),
    }));
  };

  const addExerciseDraft = (dayId) => {
    const rows = Object.values(workoutDraft);
    const existing = rows.filter((r) => r.day_id === dayId && !r.__deleted);
    const nextOrd = existing.length ? Math.max(...existing.map((r) => r.ord || 0)) + 1 : 1;
    const tmpId = makeTmpId();
    setWorkoutDraft((prev) => ({
      ...prev,
      [tmpId]: {
        id: tmpId,
        day_id: dayId,
        ord: nextOrd,
        name: '',
        sets: 3,
        rep_min: 8,
        rep_max: 12,
        rest_sec: 90,
        __new: true,
        __deleted: false,
      },
    }));
  };

  const deleteExerciseDraft = (exerciseId) => {
    setWorkoutDraft((prev) => {
      const row = prev[exerciseId];
      if (!row) return prev;
      if (row.__new) {
        const next = { ...prev };
        delete next[exerciseId];
        return next;
      }
      return { ...prev, [exerciseId]: { ...row, __deleted: true } };
    });
  };

  const saveWorkouts = async () => {
    if (!editWorkouts) return;
    setSavingWorkouts(true);
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const dayRows = Object.values(workoutDayDraft);
      const rows = Object.values(workoutDraft);
      const toDelete = rows.filter((r) => r.__deleted && !r.__new && r.id);
      const toUpdate = rows.filter((r) => !r.__deleted && !r.__new && r.id);
      const toInsert = rows.filter((r) => !r.__deleted && r.__new);
      const insertedRows = [];

      if (dayRows.length) {
        const dayPayloads = dayRows.map((d) => {
          const basePayload = d.raw_payload && typeof d.raw_payload === 'object' ? { ...d.raw_payload } : {};
          const trimmedNotes = typeof d.notes === 'string' ? d.notes.trim() : '';
          if (trimmedNotes) {
            basePayload.notes = trimmedNotes;
          } else {
            delete basePayload.notes;
          }
          return {
            id: d.id,
            display_name: d.display_name || null,
            display_focus: d.display_focus || null,
            display_duration_min: toNumOrNull(d.display_duration_min),
            display_type: d.display_type || null,
            raw_payload: basePayload,
          };
        }).filter((d) => d.id != null);
        const dayResults = await runWithTimeout(
          `Updating ${dayPayloads.length} workout day rows`,
          () => Promise.all(dayPayloads.map((payload) => (
            supabase
              .from('workout_days')
              .update({
                display_name: payload.display_name,
                display_focus: payload.display_focus,
                display_duration_min: payload.display_duration_min,
                display_type: payload.display_type,
                raw_payload: payload.raw_payload,
              })
              .eq('id', payload.id)
              .select('id')
              .maybeSingle()
          ))),
        );
        const dayErr = Array.isArray(dayResults)
          ? dayResults.find((result) => result?.error)?.error
          : null;
        if (dayErr) {
          throw new Error(`Workout day bulk update failed: ${formatDbError(dayErr)}`);
        }
      }

      if (toDelete.length) {
        const deleteIds = toDelete.map((r) => r.id).filter(Boolean);
        const { error: dErr } = await runWithTimeout(
          `Deleting ${deleteIds.length} exercise rows`,
          () => supabase.from('exercises').delete().in('id', deleteIds),
        );
        if (dErr) {
          throw new Error(`Exercise delete failed: ${formatDbError(dErr)}`);
        }
      }

      let updatedExerciseRows = [];
      if (toUpdate.length) {
        const updatePayloads = toUpdate.map((r) => ({
          id: r.id,
          day_id: r.day_id,
          name: r.name || '',
          sets: toNumOrNull(r.sets),
          rep_min: toNumOrNull(r.rep_min),
          rep_max: toNumOrNull(r.rep_max),
          rest_sec: toNumOrNull(r.rest_sec),
        }));
        const { data: updatedRows, error: uErr } = await runWithTimeout(
          `Updating ${updatePayloads.length} exercise rows`,
          () => supabase
            .from('exercises')
            .upsert(updatePayloads, { onConflict: 'id' })
            .select('id, day_id, ord, name, ex_type, sets, rep_min, rep_max, tempo, rir, rest_sec, created_at'),
        );
        if (uErr) {
          throw new Error(`Exercise bulk update failed: ${formatDbError(uErr)}`);
        }
        updatedExerciseRows = Array.isArray(updatedRows) ? updatedRows : [];
      }

      if (toInsert.length) {
        const payloads = toInsert.map((r) => ({
          day_id: r.day_id,
          ord: r.ord ?? 1,
          name: r.name || 'New exercise',
          sets: toNumOrNull(r.sets) ?? 3,
          rep_min: toNumOrNull(r.rep_min) ?? 8,
          rep_max: toNumOrNull(r.rep_max) ?? 12,
          rest_sec: toNumOrNull(r.rest_sec) ?? 90,
        }));
        const { data: inserted, error: iErr } = await runWithTimeout(
          `Inserting ${payloads.length} exercise rows`,
          () => supabase
            .from('exercises')
            .insert(payloads)
            .select('id, day_id, ord, name, ex_type, sets, rep_min, rep_max, tempo, rir, rest_sec, created_at'),
        );
        if (iErr) {
          throw new Error(`Exercise insert failed: ${formatDbError(iErr)}`);
        }
        if (Array.isArray(inserted)) insertedRows.push(...inserted);
      }

      const deletedIds = new Set(toDelete.map((r) => r.id));
      const fallbackUpdatedRows = toUpdate.map((r) => ({
        id: r.id,
        day_id: r.day_id,
        name: r.name || '',
        sets: toNumOrNull(r.sets),
        rep_min: toNumOrNull(r.rep_min),
        rep_max: toNumOrNull(r.rep_max),
        rest_sec: toNumOrNull(r.rest_sec),
      }));
      const updatedById = new Map(
        (updatedExerciseRows.length ? updatedExerciseRows : fallbackUpdatedRows).map((r) => ([
          r.id,
          r,
        ])),
      );

      setExercisesByDayId((prev) => {
        const next = new Map();
        prev.forEach((list, dayId) => {
          const updatedList = (list || [])
            .filter((ex) => !deletedIds.has(ex.id))
            .map((ex) => {
              const update = updatedById.get(ex.id);
              return update ? { ...ex, ...update } : ex;
            });
          next.set(dayId, updatedList);
        });
        insertedRows.forEach((row) => {
          const list = next.get(row.day_id) ? [...next.get(row.day_id)] : [];
          list.push(row);
          list.sort((a, b) => (a.ord || 0) - (b.ord || 0));
          next.set(row.day_id, list);
        });
        return next;
      });

      if (dayRows.length) {
        setWorkoutDays((prev) => (
          (prev || []).map((day) => {
            const draft = workoutDayDraft[day.id];
            if (!draft) return day;
            const basePayload = draft.raw_payload && typeof draft.raw_payload === 'object' ? { ...draft.raw_payload } : {};
            const trimmedNotes = typeof draft.notes === 'string' ? draft.notes.trim() : '';
            if (trimmedNotes) {
              basePayload.notes = trimmedNotes;
            } else {
              delete basePayload.notes;
            }
            return {
              ...day,
              display_name: draft.display_name || null,
              display_focus: draft.display_focus || null,
              display_duration_min: toNumOrNull(draft.display_duration_min),
              display_type: draft.display_type || null,
              raw_payload: basePayload,
            };
          })
        ));
      }

      setMessage('Workout plan updated');
      setEditWorkouts(false);
      setWorkoutDraft({});
      setWorkoutDayDraft({});
    } catch (e) {
      console.error('saveWorkouts failed', e);
      setError(formatDbError(e, 'Unable to update workout plan.'));
    } finally {
      setBusy(false);
      setSavingWorkouts(false);
    }
  };

  const startEditMealPlan = () => {
    const next = {};
    (meals || []).forEach((m) => {
      const num = Number(m.meal_number);
      const payloadMeal = Number.isFinite(num) ? mealPayloadByNumber.get(num) : null;
      const foods = Array.isArray(payloadMeal?.foods) ? payloadMeal.foods : [];
      const foodTotals = sumFoodMacros(foods);
      const fallbackMacros = foodTotals || getPayloadMealMacros(payloadMeal) || null;
      const seededMacros = hasMacros(m.macros) ? m.macros : (normalizeMealMacros(fallbackMacros) || m.macros || {});
      if (seededMacros && (seededMacros.calories == null || seededMacros.calories === '')) {
        const derived = deriveCaloriesFromMacros(seededMacros.protein_g, seededMacros.carbs_g, seededMacros.fats_g);
        if (derived != null) {
          seededMacros.calories = derived;
        }
      }
      next[m.id] = { ...m, macros: seededMacros, __new: false, __deleted: false };
    });
    mealPayloadEditBaseRef.current = cloneJson(mealDayPayload);
    setMealFoodOpsDraft([]);
    setMealDraft(next);
    setEditMealPlan(true);
  };

  const cancelEditMealPlan = () => {
    const basePayload = cloneJson(mealPayloadEditBaseRef.current);
    if (basePayload) {
      syncMealDayPayload(basePayload);
    }
    resetSwapModal();
    mealPayloadEditBaseRef.current = null;
    setMealFoodOpsDraft([]);
    setEditMealPlan(false);
    setMealDraft({});
  };

  const patchMealDraft = (mealId, patch) => {
    setMealDraft((prev) => ({
      ...prev,
      [mealId]: { ...(prev[mealId] || {}), ...patch },
    }));
  };

  const addMealDraft = () => {
    if (!mealDay?.id) return;
    const rows = Object.values(mealDraft);
    const maxNum = rows.length ? Math.max(...rows.map((r) => toNumOrNull(r.meal_number) || 0)) : (meals.length ? Math.max(...meals.map((m) => m.meal_number || 0)) : 0);
    const nextNumber = (maxNum || 0) + 1;
    const tmpId = makeTmpId();
    setMealDraft((prev) => ({
      ...prev,
      [tmpId]: {
        id: tmpId,
        day_id: mealDay.id,
        meal_number: nextNumber,
        name: `Meal ${nextNumber}`,
        time_window: '',
        purpose: '',
        notes: '',
        macros: {},
        __new: true,
        __deleted: false,
      },
    }));
  };

  const deleteMealDraft = (mealId) => {
    setMealDraft((prev) => {
      const row = prev[mealId];
      if (!row) return prev;
      if (row.__new) {
        const next = { ...prev };
        delete next[mealId];
        return next;
      }
      return { ...prev, [mealId]: { ...row, __deleted: true } };
    });
  };

  const saveMealPlan = async () => {
    if (!editMealPlan) return;
    setSavingMeals(true);
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (mealFoodOpsDraft.length) {
        const accessToken = await getAccessToken();
        let latestPayload = null;
        try {
          const mealOpsTimeoutMs = Math.max(MEAL_MUTATION_TIMEOUT_MS, mealFoodOpsDraft.length * 6000);
          const json = await callBackendWithTimeout('/meal-plan/apply-ops', {
            userId: memberId,
            accessToken,
            ops: mealFoodOpsDraft,
          }, mealOpsTimeoutMs);
          latestPayload = parseMealDayFromResponse(json?.meal_day);
        } catch (batchErr) {
          // Backward-compatible fallback while API and frontend deployments roll out.
          for (const op of mealFoodOpsDraft) {
            let json = null;
            if (op.type === 'add') {
              json = await callBackendWithTimeout('/meal-plan/add-food', {
                userId: memberId,
                accessToken,
                mealNumber: op.mealNumber,
                category: op.category,
                food: op.food,
              }, MEAL_MUTATION_TIMEOUT_MS);
            } else if (op.type === 'swap') {
              json = await callBackendWithTimeout('/meal-plan/swap', {
                userId: memberId,
                accessToken,
                mealNumber: op.mealNumber,
                category: op.category,
                categoryIndex: op.categoryIndex,
                swap: op.swap,
              }, MEAL_MUTATION_TIMEOUT_MS);
            } else if (op.type === 'remove') {
              json = await callBackendWithTimeout('/meal-plan/remove-food', {
                userId: memberId,
                accessToken,
                mealNumber: op.mealNumber,
                foodIndex: op.foodIndex,
                category: op.category,
                categoryIndex: op.categoryIndex,
                itemName: op.itemName,
              }, MEAL_MUTATION_TIMEOUT_MS);
            }
            const payload = parseMealDayFromResponse(json?.meal_day);
            if (payload) latestPayload = payload;
          }
        }
        if (latestPayload) {
          syncMealDayPayload(latestPayload);
        }
        setMealFoodOpsDraft([]);
      }

      const rows = Object.values(mealDraft);
      const toDelete = rows.filter((r) => r.__deleted && !r.__new && r.id);
      const toUpdate = rows.filter((r) => !r.__deleted && !r.__new && r.id);
      const toInsert = rows.filter((r) => !r.__deleted && r.__new);
      const insertedMeals = [];

      if (toDelete.length) {
        const deleteIds = toDelete.map((r) => r.id).filter(Boolean);
        const { error: dErr } = await runWithTimeout(
          `Deleting ${deleteIds.length} meal rows`,
          () => supabase.from('meals').delete().in('id', deleteIds),
        );
        if (dErr) {
          throw new Error(`Meal delete failed: ${formatDbError(dErr)}`);
        }
      }

      const updatedById = new Map();
      if (toUpdate.length) {
        const updatePayloads = toUpdate.map((r) => {
          const macros = normalizeMealMacros(r.macros);
          return {
            id: r.id,
            day_id: r.day_id,
            name: r.name || '',
            time_window: r.time_window || '',
            purpose: r.purpose || '',
            notes: r.notes || '',
            meal_number: toNumOrNull(r.meal_number),
            macros,
          };
        });
        const { data: updatedRows, error: uErr } = await runWithTimeout(
          `Updating ${updatePayloads.length} meal rows`,
          () => supabase
            .from('meals')
            .upsert(updatePayloads, { onConflict: 'id' })
            .select('id, day_id, meal_number, name, time_window, macros, purpose, notes, swap_options, created_at'),
        );
        if (uErr) {
          throw new Error(`Meal bulk update failed: ${formatDbError(uErr)}`);
        }
        const source = Array.isArray(updatedRows) && updatedRows.length ? updatedRows : updatePayloads;
        source.forEach((row) => {
          if (row?.id == null) return;
          updatedById.set(row.id, row);
        });
      }

      if (toInsert.length) {
        const payloads = toInsert.map((r) => {
          const macros = normalizeMealMacros(r.macros);
          return {
          day_id: r.day_id,
          meal_number: toNumOrNull(r.meal_number) ?? 1,
          name: r.name || `Meal ${r.meal_number ?? ''}`,
          time_window: r.time_window || '',
          purpose: r.purpose || '',
          notes: r.notes || '',
          macros: macros || {},
          };
        });
        const { data: inserted, error: iErr } = await runWithTimeout(
          `Inserting ${payloads.length} meal rows`,
          () => supabase
            .from('meals')
            .insert(payloads)
            .select('id, day_id, meal_number, name, time_window, macros, purpose, notes, swap_options, created_at'),
        );
        if (iErr) {
          throw new Error(`Meal insert failed: ${formatDbError(iErr)}`);
        }
        if (Array.isArray(inserted)) insertedMeals.push(...inserted);
      }

      const deletedIds = new Set(toDelete.map((r) => r.id));
      setMeals((prev) => {
        const base = (prev || [])
          .filter((m) => !deletedIds.has(m.id))
          .map((m) => {
            const updated = updatedById.get(m.id);
            return updated ? { ...m, ...updated } : m;
          });
        const merged = [...base, ...insertedMeals];
        merged.sort((a, b) => (toNumOrNull(a.meal_number) || 0) - (toNumOrNull(b.meal_number) || 0));
        return merged;
      });

      setMessage('Meal plan updated');
      setEditMealPlan(false);
      setMealDraft({});
      setMealFoodOpsDraft([]);
      mealPayloadEditBaseRef.current = null;
    } catch (e) {
      console.error('saveMealPlan failed', e);
      setError(formatDbError(e, 'Unable to update meal plan.'));
    } finally {
      setBusy(false);
      setSavingMeals(false);
    }
  };

  if (loading) {
    return (
      <>
        <NavBar />
        <LoadingAnimation fullScreen />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <CoachAssessmentModal
        isOpen={assessmentModalOpen}
        onClose={() => setAssessmentModalOpen(false)}
        onUpdated={(next, nextNutritionPrefs = null) => {
          setAssessment(next);
          if (nextNutritionPrefs) {
            setNutritionPrefs({
              proteins: Array.isArray(nextNutritionPrefs.proteins) ? nextNutritionPrefs.proteins : [],
              carbs: Array.isArray(nextNutritionPrefs.carbs) ? nextNutritionPrefs.carbs : [],
              fats: Array.isArray(nextNutritionPrefs.fats) ? nextNutritionPrefs.fats : [],
            });
          }
        }}
        memberName={memberName}
        assessmentRow={assessment}
      />
      {exercisePicker.open && (
        <div className="coach-modal-overlay" onClick={() => !exerciseFormSaving && closeExercisePicker()}>
          <div className="coach-modal coach-exercise-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coach-modal-header">
              <div>
                <div className="coach-modal-title">{exerciseFormOpen ? 'Add new exercise' : 'Find exercise'}</div>
                <div className="coach-muted">
                  {exerciseFormOpen ? 'Create a new exercise entry for the library.' : 'Search the library or add a custom exercise.'}
                </div>
              </div>
              <button
                type="button"
                className="coach-btn secondary"
                onClick={closeExercisePicker}
                disabled={exerciseFormSaving}
              >
                Close
              </button>
            </div>
            <div className="coach-modal-body" ref={swapModalBodyRef}>
              {exerciseLibraryError && !exerciseFormOpen && (
                <div className="coach-alert error">{exerciseLibraryError}</div>
              )}
              {exerciseFormError && <div className="coach-alert error">{exerciseFormError}</div>}
              {exerciseFormOpen ? (
                <div className="coach-exercise-form">
                  <div className="coach-exercise-grid">
                    <div className="coach-row full-span">
                      <label className="coach-label">Name</label>
                      <input
                        className="coach-input"
                        value={exerciseForm.name}
                        onChange={(e) => {
                          const nextName = e.target.value;
                          setExerciseForm((prev) => ({
                            ...prev,
                            name: nextName,
                          }));
                        }}
                        placeholder="Exercise name"
                        disabled={exerciseFormSaving}
                      />
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Level</label>
                      <div className="coach-select-wrap">
                        <select
                          className="coach-select-input"
                          value={exerciseForm.level}
                          onChange={(e) => setExerciseForm((prev) => ({ ...prev, level: e.target.value }))}
                          disabled={exerciseFormSaving}
                        >
                          <option value="">Select level</option>
                          {EXERCISE_LEVEL_OPTIONS.map((opt) => (
                            <option key={`level-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Mechanic</label>
                      <div className="coach-select-wrap">
                        <select
                          className="coach-select-input"
                          value={exerciseForm.mechanic}
                          onChange={(e) => setExerciseForm((prev) => ({ ...prev, mechanic: e.target.value }))}
                          disabled={exerciseFormSaving}
                        >
                          <option value="">Select mechanic</option>
                          {EXERCISE_MECHANIC_OPTIONS.map((opt) => (
                            <option key={`mechanic-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Equipment</label>
                      <div className="coach-select-wrap">
                        <select
                          className="coach-select-input"
                          value={exerciseForm.equipment}
                          onChange={(e) => setExerciseForm((prev) => ({ ...prev, equipment: e.target.value }))}
                          disabled={exerciseFormSaving}
                        >
                          <option value="">Select equipment</option>
                          {EXERCISE_EQUIPMENT_OPTIONS.map((opt) => (
                            <option key={`equipment-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Category</label>
                      <div className="coach-select-wrap">
                        <select
                          className="coach-select-input"
                          value={exerciseForm.category}
                          onChange={(e) => setExerciseForm((prev) => ({ ...prev, category: e.target.value }))}
                          disabled={exerciseFormSaving}
                        >
                          <option value="">Select category</option>
                          {EXERCISE_CATEGORY_OPTIONS.map((opt) => (
                            <option key={`category-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Force</label>
                      <div className="coach-select-wrap">
                        <select
                          className="coach-select-input"
                          value={exerciseForm.force}
                          onChange={(e) => setExerciseForm((prev) => ({ ...prev, force: e.target.value }))}
                          disabled={exerciseFormSaving}
                        >
                          <option value="">Select force</option>
                          {EXERCISE_FORCE_OPTIONS.map((opt) => (
                            <option key={`force-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Primary muscles</label>
                    <div className="coach-chip-list">
                      {EXERCISE_MUSCLE_OPTIONS.map((muscle) => (
                        <button
                          key={`primary-${muscle}`}
                          type="button"
                          className={`coach-chip ${exerciseForm.primaryMuscles.includes(muscle) ? 'selected' : ''}`}
                          onClick={() => toggleExerciseMuscle('primaryMuscles', muscle)}
                          disabled={exerciseFormSaving}
                        >
                          {muscle}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Secondary muscles</label>
                    <div className="coach-chip-list">
                      <button
                        type="button"
                        className={`coach-chip ${exerciseForm.secondaryMuscles.length === 0 ? 'selected' : ''}`}
                        onClick={() => setExerciseForm((prev) => ({ ...prev, secondaryMuscles: [] }))}
                        disabled={exerciseFormSaving}
                      >
                        None
                      </button>
                      {EXERCISE_MUSCLE_OPTIONS.map((muscle) => (
                        <button
                          key={`secondary-${muscle}`}
                          type="button"
                          className={`coach-chip ${exerciseForm.secondaryMuscles.includes(muscle) ? 'selected' : ''}`}
                          onClick={() => toggleExerciseMuscle('secondaryMuscles', muscle)}
                          disabled={exerciseFormSaving}
                        >
                          {muscle}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Instructions (one per line)</label>
                    <textarea
                      className="coach-input coach-textarea"
                      value={exerciseForm.instructions}
                      onChange={(e) => setExerciseForm((prev) => ({ ...prev, instructions: e.target.value }))}
                      placeholder="Step 1\nStep 2\nStep 3"
                      disabled={exerciseFormSaving}
                      rows={4}
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Images (upload required)</label>
                    <input
                      className="coach-input"
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={exerciseFormSaving || exerciseUploading}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) {
                          uploadExerciseImages(files);
                        }
                        e.target.value = '';
                      }}
                    />
                    {exerciseUploading && <div className="coach-muted">Uploading images...</div>}
                    {exerciseUploadError && <div className="coach-alert error">{exerciseUploadError}</div>}
                    {(exerciseForm.images || []).length > 0 && (
                      <div className="coach-upload-list">
                        {exerciseForm.images.map((path) => {
                          const label = path.split('/').pop() || path;
                          return (
                            <div key={path} className="coach-upload-item">
                              <span className="coach-upload-name">{label}</span>
                              <button
                                type="button"
                                className="coach-btn secondary compact"
                                onClick={() => removeExerciseImage(path)}
                                disabled={exerciseFormSaving || exerciseUploading}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="coach-actions">
                    <button
                      type="button"
                      className="coach-btn"
                      onClick={saveExerciseForm}
                      disabled={exerciseFormSaving}
                    >
                      {exerciseFormSaving ? 'Saving...' : 'Save exercise'}
                    </button>
                    <button
                      type="button"
                      className="coach-btn secondary"
                      onClick={() => {
                        setExerciseFormOpen(false);
                        setExerciseFormError('');
                      }}
                      disabled={exerciseFormSaving}
                    >
                      Back to list
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="coach-exercise-toolbar">
                    <input
                      className="coach-input slim coach-exercise-search"
                      value={exerciseSearch}
                      onChange={(e) => setExerciseSearch(e.target.value)}
                      placeholder="Search exercises"
                      disabled={exerciseLibraryLoading}
                    />
                    <button
                      type="button"
                      className="coach-btn"
                      onClick={() => {
                        resetExerciseForm();
                        setExerciseFormOpen(true);
                      }}
                      disabled={exerciseLibraryLoading}
                    >
                      Add New Exercise
                    </button>
                  </div>
                  {exerciseLibraryLoading ? (
                    <div className="coach-muted">Loading exercise library...</div>
                  ) : filteredExerciseLibrary.length === 0 ? (
                    <div className="coach-muted">No exercises match that search.</div>
                  ) : (
                    <div className="coach-exercise-list">
                      {filteredExerciseLibrary.map((exercise) => {
                        const metaParts = [exercise.category, exercise.equipment].filter(Boolean);
                        const itemId = exercise.id || exercise.name;
                        const isSelected = selectedExercise && (selectedExercise.id || selectedExercise.name) === itemId;
                        const showDetails = exerciseDetailsId === itemId;
                        return (
                          <div
                            key={`exercise-${itemId}`}
                            role="button"
                            tabIndex={0}
                            className={`coach-exercise-item ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedExercise((prev) => (isSelected ? null : exercise))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedExercise((prev) => (isSelected ? null : exercise));
                              }
                            }}
                          >
                            <div>
                              <div className="coach-exercise-name">{exercise.name}</div>
                              {metaParts.length > 0 && (
                                <div className="coach-exercise-meta">{metaParts.join(' | ')}</div>
                              )}
                              {showDetails && (
                                <div className="coach-exercise-detail">
                                  <div>Force: {exercise.force || '-'}</div>
                                  <div>Level: {exercise.level || '-'}</div>
                                  <div>Mechanic: {exercise.mechanic || '-'}</div>
                                  <div>Primary: {(exercise.primaryMuscles || []).join(', ') || '-'}</div>
                                  <div>Secondary: {(exercise.secondaryMuscles || []).join(', ') || '-'}</div>
                                </div>
                              )}
                            </div>
                            <div className="coach-exercise-pill">
                              <span className={`coach-exercise-source ${exercise.source === 'db' ? 'custom' : 'preset'}`}>
                                {exercise.source === 'db' ? 'Custom' : 'Preset'}
                              </span>
                              <button
                                type="button"
                                className="coach-exercise-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExerciseDetailsId(showDetails ? null : itemId);
                                }}
                              >
                                {showDetails ? 'See less' : 'See more'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedExercise && (
                    <div className="coach-actions">
                      <button
                        type="button"
                        className="coach-btn"
                        onClick={() => applyExerciseSelection(selectedExercise)}
                      >
                        Confirm Selected Exercise
                      </button>
                    </div>
                  )}
                </>
              )}
              <div ref={swapModalBottomRef} />
            </div>
          </div>
        </div>
      )}
      {swapModal.open && (
        <div
          className="coach-modal-overlay"
          onClick={() => !swapModal.loading && resetSwapModal()}
        >
          <div className="coach-modal coach-swap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coach-modal-header">
              <div>
                <div className="coach-modal-title">{isAddMode ? 'Add ingredient' : 'Swap item'}</div>
                <div className="coach-muted">
                  {isAddMode
                    ? (swapModal.context?.mealNumber != null ? `Adding to meal #${swapModal.context.mealNumber}` : 'Add a new ingredient')
                    : (swapModal.context?.currentLabel ? `Replacing: ${swapModal.context.currentLabel}` : 'Choose a replacement')}
                </div>
                {isAddMode && addMealMacroLine && (
                  <div className="coach-muted" style={{ marginTop: 4, fontSize: 12 }}>
                    Current meal macros: {addMealMacroLine}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="coach-btn secondary"
                onClick={resetSwapModal}
                disabled={swapModal.loading}
              >
                Close
              </button>
            </div>
            <div className="coach-modal-body">
              {swapModal.error && <div className="coach-alert error">{swapModal.error}</div>}
              <div className="coach-swap-section">
                <div className="coach-swap-section-title">{isAddMode ? 'Ingredient details' : 'Manual swap'}</div>
                {swapNotice && <div className="coach-alert success coach-swap-notice">{swapNotice}</div>}
                <div className="coach-swap-grid">
                  <div className="coach-row">
                    <label className="coach-label" htmlFor="swap-item-name">Food</label>
                    <input
                      id="swap-item-name"
                      className="coach-input slim"
                      value={swapForm.item}
                      onChange={(e) => setSwapForm((prev) => ({ ...prev, item: e.target.value, itemModel: null }))}
                      placeholder="Food name"
                      disabled={swapModal.loading}
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label" htmlFor="swap-item-amount">Amount</label>
                    <input
                      id="swap-item-amount"
                      className="coach-input slim"
                      value={swapForm.amount}
                      onChange={(e) => {
                        const nextAmount = e.target.value;
                        setSwapForm((prev) => {
                          const next = { ...prev, amount: nextAmount };
                          const baseMacros = normalizeNutrition(
                            prev.itemModel?.macros || null,
                            prev.itemModel?.metadata || null,
                          );
                          const baseAmount = prev.itemModel?.amount
                            || prev.itemModel?.metadata?.serving_size
                            || null;
                          if (baseMacros) {
                            const scaled = scaleMacrosForAmount(baseMacros, nextAmount, baseAmount);
                            next.calories = scaled?.calories != null ? formatMacroValue(scaled.calories) : '';
                            next.protein_g = scaled?.protein_g != null ? formatMacroValue(scaled.protein_g) : '';
                            next.carbs_g = scaled?.carbs_g != null ? formatMacroValue(scaled.carbs_g) : '';
                            next.fats_g = scaled?.fats_g != null ? formatMacroValue(scaled.fats_g) : '';
                          }
                          return next;
                        });
                      }}
                      placeholder="e.g. 150 g"
                      disabled={swapModal.loading}
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label" htmlFor="swap-item-calories">Calories</label>
                    <input
                      id="swap-item-calories"
                      className="coach-input slim"
                      value={swapForm.calories}
                      onChange={(e) => setSwapForm((prev) => ({ ...prev, calories: e.target.value }))}
                      placeholder="kcal"
                      disabled={swapModal.loading}
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label" htmlFor="swap-item-protein">Protein (g)</label>
                    <input
                      id="swap-item-protein"
                      className="coach-input slim"
                      value={swapForm.protein_g}
                      onChange={(e) => setSwapForm((prev) => ({ ...prev, protein_g: e.target.value }))}
                      placeholder="0"
                      disabled={swapModal.loading}
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label" htmlFor="swap-item-carbs">Carbs (g)</label>
                    <input
                      id="swap-item-carbs"
                      className="coach-input slim"
                      value={swapForm.carbs_g}
                      onChange={(e) => setSwapForm((prev) => ({ ...prev, carbs_g: e.target.value }))}
                      placeholder="0"
                      disabled={swapModal.loading}
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label" htmlFor="swap-item-fats">Fats (g)</label>
                    <input
                      id="swap-item-fats"
                      className="coach-input slim"
                      value={swapForm.fats_g}
                      onChange={(e) => setSwapForm((prev) => ({ ...prev, fats_g: e.target.value }))}
                      placeholder="0"
                      disabled={swapModal.loading}
                    />
                  </div>
                </div>
                <div className="coach-swap-hint">Leave macros blank to use the selected option macros.</div>
                {swapForm.itemModel?.macros && !swapForm.protein_g && !swapForm.carbs_g && !swapForm.fats_g && !swapForm.calories && (
                  <div className="coach-muted">
                    Option macros loaded{swapForm.itemModel.amount ? ` (per ${swapForm.itemModel.amount})` : ''}.
                  </div>
                )}
              <div className="coach-actions">
                <button type="button" className="coach-btn" onClick={applySwap} disabled={swapModal.loading || busy}>
                  {swapModal.loading ? 'Saving...' : (isAddMode ? 'Add ingredient' : 'Apply swap')}
                </button>
              </div>
            </div>

            <div className="coach-swap-section">
              <div
                className="coach-swap-section-title"
                style={isAddMode ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 } : undefined}
              >
                <span>
                  {isAddMode
                    ? 'All ingredients'
                    : `Available ${formatFoodCategoryLabel(swapModal.context?.category) || 'options'}`}
                </span>
                {isAddMode && (
                  <button
                    type="button"
                    className="coach-btn secondary"
                    onClick={() => setIngredientFormOpen(true)}
                    disabled={ingredientCatalogLoading || swapModal.loading}
                  >
                    Add New Ingredient
                  </button>
                )}
              </div>
              {isAddMode ? (
                <>
                  <div className="coach-swap-search">
                    <input
                      className="coach-input slim"
                      value={optionQuery}
                      onChange={(e) => setOptionQuery(e.target.value)}
                      placeholder="Search all ingredients"
                      disabled={swapModal.loading}
                    />
                  </div>
                  {ingredientCatalogLoading && <div className="coach-muted">Loading ingredients...</div>}
                  {ingredientCatalogError && !ingredientCatalogLoading && (
                    <div className="coach-alert error">{ingredientCatalogError}</div>
                  )}
                  {!ingredientCatalogLoading && !ingredientCatalogError && ingredientCatalog.length > 0 && filteredIngredients.length === 0 && (
                    <div className="coach-muted">No ingredients match that search.</div>
                  )}
                  {filteredIngredients.length > 0 && (
                    <div className="coach-swap-options coach-swap-options-scroll">
                      {filteredIngredients.map((opt, idx) => {
                        const label = getIngredientLabel(opt) || `Result ${idx + 1}`;
                        return (
                          <button
                            key={`${label}-${idx}`}
                            type="button"
                            className={`coach-swap-option coach-swap-option-button ${selectedOption === label ? 'selected' : ''}`}
                            onClick={() => handleSelectOption(opt)}
                            disabled={swapModal.loading}
                          >
                            <span className="coach-swap-option-name">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {!ingredientCatalogLoading && !ingredientCatalogError && ingredientCatalog.length === 0 && (
                    <div className="coach-muted">No ingredients available.</div>
                  )}
                </>
              ) : (
                <>
                  {swapModal.options.length === 0 ? (
                    <div className="coach-muted">No saved options for this category yet.</div>
                  ) : (
                    <>
                      <div className="coach-swap-search">
                        <input
                          className="coach-input slim"
                          value={optionQuery}
                          onChange={(e) => setOptionQuery(e.target.value)}
                          placeholder={`Search ${formatFoodCategoryLabel(swapModal.context?.category) || 'items'}`}
                          disabled={swapModal.loading}
                        />
                      </div>
                      {filteredOptions.length === 0 ? (
                        <div className="coach-muted">No options match that search.</div>
                      ) : (
                        <div className="coach-swap-options coach-swap-options-scroll">
                          {filteredOptions.map((opt, idx) => (
                            <button
                              key={`${opt}-${idx}`}
                              type="button"
                              className={`coach-swap-option coach-swap-option-button ${selectedOption === opt ? 'selected' : ''}`}
                              onClick={() => handleSelectOption(opt)}
                              disabled={swapModal.loading}
                            >
                              <span className="coach-swap-option-name">{opt}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
                {selectedOption && showOptionDetail && (
                  <div className="coach-swap-detail">
                    <div className="coach-swap-detail-title">{selectedOption}</div>
                    {optionLoading && <div className="coach-muted">Loading macros...</div>}
                    {optionError && <div className="coach-alert error">{optionError}</div>}
                    {!optionLoading && !optionError && (
                      <>
                        {optionBaseAmount && (
                          <div className="coach-swap-amount">
                            <strong>Amount:</strong> {ensureAmountLabel(optionBaseAmount)}
                          </div>
                        )}
                        {optionNutritionEntries.length > 0 && (
                          <div className="coach-swap-nutrition">
                            <div className="coach-swap-subtitle">Nutrient info:</div>
                            <div className="nutrition-pill-row">
                              {optionNutritionEntries.map((entry) => (
                                <span
                                  key={`${selectedOption}-${entry.label}`}
                                  className={`nutrition-pill nutrition-pill-${entry.key || 'default'}`}
                                >
                                  {entry.label}: {entry.value}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {optionMetadataEntries.length > 0 && (
                          <div className="coach-swap-metadata">
                            <div className="coach-swap-subtitle">Full nutrition profile:</div>
                            <div className="metadata-grid">
                              {optionMetadataEntries.map((entry) => (
                                <div key={`${selectedOption}-${entry.key}`} className="metadata-grid-row">
                                  <span className="metadata-label">{entry.label}</span>
                                  <span className="metadata-value">{entry.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {optionNutritionEntries.length === 0 && optionMetadataEntries.length === 0 && (
                          <div className="coach-muted">No nutrition data found for this item.</div>
                        )}
                        <div className="coach-row coach-swap-calc">
                          <label className="coach-label" htmlFor="swap-calc-amount">Amount (g)</label>
                          <input
                            id="swap-calc-amount"
                            className="coach-input slim"
                            value={optionCalcAmount}
                            onChange={(e) => setOptionCalcAmount(e.target.value)}
                            placeholder={optionBaseAmount ? ensureAmountLabel(optionBaseAmount) : 'e.g. 150 g'}
                          />
                        </div>
                        {calcNutritionEntries.length > 0 && optionCalcAmount && (
                          <div className="coach-swap-nutrition">
                            <div className="coach-swap-subtitle">Total for {ensureAmountLabel(optionCalcAmount)}:</div>
                            <div className="nutrition-pill-row">
                              {calcNutritionEntries.map((entry) => (
                                <span
                                  key={`${selectedOption}-calc-${entry.label}`}
                                  className={`nutrition-pill nutrition-pill-${entry.key || 'default'}`}
                                >
                                  {entry.label}: {entry.value}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    <button
                      type="button"
                      className="coach-btn secondary"
                      onClick={applyOptionToSwapForm}
                      disabled={!selectedOption || optionLoading}
                    >
                      Use
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {ingredientFormOpen && (
        <div className="coach-modal-overlay" onClick={() => !ingredientFormSaving && setIngredientFormOpen(false)}>
          <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coach-modal-header">
              <div>
                <div className="coach-modal-title">Add New Ingredient</div>
                <div className="coach-muted">Save a custom ingredient to the database.</div>
              </div>
              <button
                type="button"
                className="coach-btn secondary"
                onClick={() => setIngredientFormOpen(false)}
                disabled={ingredientFormSaving}
              >
                Close
              </button>
            </div>
            <div className="coach-modal-body">
              <div className="coach-form">
                <div className="coach-grid-2">
                  <div className="coach-row">
                    <label className="coach-label">Ingredient name</label>
                    <input
                      className="coach-input"
                      value={ingredientForm.name}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Chicken Thigh (boneless)"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Serving size</label>
                    <input
                      className="coach-input"
                      value={ingredientForm.serving_size}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, serving_size: e.target.value }))}
                      placeholder="e.g. 28g"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Calories</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.calories}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, calories: e.target.value }))}
                      placeholder="e.g. 56"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Protein (g)</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.protein_g}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, protein_g: e.target.value }))}
                      placeholder="e.g. 7.3"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Carbs (g)</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.carbs_g}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, carbs_g: e.target.value }))}
                      placeholder="e.g. 0"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Fats (g)</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.fats_g}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, fats_g: e.target.value }))}
                      placeholder="e.g. 3"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Sodium (mg)</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.sodium_mg}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, sodium_mg: e.target.value }))}
                      placeholder="e.g. 24.6"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Potassium (mg)</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.potassium_mg}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, potassium_mg: e.target.value }))}
                      placeholder="e.g. 66.6"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Fiber (g)</label>
                    <input
                      className="coach-input"
                      type="number"
                      value={ingredientForm.fiber_g}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, fiber_g: e.target.value }))}
                      placeholder="e.g. 0"
                    />
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Category</label>
                    <select
                      className="coach-input"
                      value={ingredientForm.category}
                      onChange={(e) => setIngredientForm((prev) => ({ ...prev, category: e.target.value }))}
                    >
                      {INGREDIENT_CATEGORY_OPTIONS.map((opt) => (
                        <option key={`ing-modal-${opt.value}`} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="coach-row">
                  <label className="coach-label">Tags</label>
                  <div className="coach-actions" style={{ marginTop: 4, flexWrap: 'nowrap' }}>
                    {[
                      { key: 'complex_carb', label: 'Complex carb' },
                      { key: 'lean_protein', label: 'Lean protein' },
                      { key: 'fatty_protein', label: 'Fatty protein' },
                      { key: 'fruit', label: 'Fruit' },
                      { key: 'green_veggie', label: 'Green veggie' },
                    ].map((tag) => (
                      <label key={`tag-modal-${tag.key}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={ingredientForm[tag.key]}
                          onChange={(e) => setIngredientForm((prev) => ({ ...prev, [tag.key]: e.target.checked }))}
                        />
                        <span>{tag.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {ingredientFormError && <div className="coach-alert error">{ingredientFormError}</div>}
                <div className="coach-actions">
                  <button
                    type="button"
                    className="coach-btn"
                    onClick={saveNewIngredient}
                    disabled={ingredientFormSaving}
                  >
                    {ingredientFormSaving ? 'Saving...' : 'Save Ingredient'}
                  </button>
                  <button
                    type="button"
                    className="coach-btn secondary"
                    onClick={() => {
                      setIngredientFormOpen(false);
                      setIngredientFormError('');
                    }}
                    disabled={ingredientFormSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <main className="web-view">
        <div className="container">
          <div className="coach-page coach-page-wide">
            <div className="coach-header">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <h1 className="coach-title">{memberName}</h1>
                <Link to="/coach" className="coach-link">Back to dashboard</Link>
              </div>
              <button type="button" className="coach-btn secondary" onClick={load} disabled={busy}>Refresh</button>
            </div>

            {error && <div className="coach-alert error">{error}</div>}
            {message && (
              <div className="coach-alert success coach-alert-dismissible">
                <span>{message}</span>
                <button
                  type="button"
                  className="coach-alert-dismiss"
                  onClick={() => setMessage('')}
                  aria-label="Dismiss notification"
                >
                  ×
                </button>
              </div>
            )}

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Overview</h2>
                <button
                  type="button"
                  className="coach-btn secondary"
                  onClick={() => setAssessmentModalOpen(true)}
                  disabled={!assessment}
                  title={!assessment ? 'No assessment row yet' : 'View assessment answers'}
                >
                  Assessment
                </button>
              </div>
              <div className="coach-card-body">
                <div className="coach-kv">
                  <div><span className="coach-muted">Member ID:</span> <span className="coach-mono">{memberId}</span></div>
                  {assessment?.age != null && <div><span className="coach-muted">Age:</span> {assessment.age}</div>}
                  {assessment?.gender && <div><span className="coach-muted">Gender:</span> {assessment.gender}</div>}
                  {heightLabel && <div><span className="coach-muted">Height:</span> {heightLabel}</div>}
                  {currentWeight != null && <div><span className="coach-muted">Current weight:</span> {currentWeight} lb</div>}
                  {program?.goal && <div><span className="coach-muted">Primary goal:</span> {program.goal}</div>}
                  {primaryGoal && <div><span className="coach-muted">Assessment goal:</span> {primaryGoal}</div>}
                </div>
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Program</h2>
                {!program && (
                  <button type="button" className="coach-btn" onClick={createBlankProgram} disabled={busy}>Create Program</button>
                )}
                {program && !editProgram && (
                  <button type="button" className="coach-btn secondary" onClick={() => setEditProgram(true)} disabled={busy}>
                    Edit
                  </button>
                )}
                {program && editProgram && (
                  <div className="coach-actions" style={{ marginTop: 0 }}>
                    <button type="button" className="coach-btn" onClick={saveProgram} disabled={busy}>Save</button>
                    <button type="button" className="coach-btn secondary" onClick={cancelProgramEdit} disabled={busy}>Cancel</button>
                  </div>
                )}
              </div>

              {program && (
                <div className="coach-card-body">
                  <div className="coach-actions">
                    <button type="button" className="coach-btn" onClick={regenerateWorkout} disabled={busy}>
                      {regeneratingWorkout ? 'Regenerating Workout Plan...' : 'Regenerate Workout Plan'}
                    </button>
                    <button type="button" className="coach-btn" onClick={regenerateMeals} disabled={busy}>
                      {regeneratingMeals ? 'Regenerating Meal Plan...' : 'Regenerate Meal Plan'}
                    </button>
                    <button type="button" className="coach-btn secondary" onClick={resetWorkoutPlan} disabled={busy}>Clear Workout Plan</button>
                    <button type="button" className="coach-btn secondary" onClick={resetMeals} disabled={busy}>Clear Meal Plan</button>
                  </div>
                  {isRegenerating && (
                    <div className="coach-regenerate-status" role="status" aria-live="polite">
                      <LoadingAnimation compact />
                      <span className="coach-regenerate-text">{regenerateStatusLabel}</span>
                    </div>
                  )}

                  {!editProgram && (
                    <div className="coach-kv" style={{ marginTop: 10 }}>
                      {program?.goal && <div><span className="coach-muted">Goal:</span> {program.goal}</div>}
                      {macroSummary && <div><span className="coach-muted">Macros:</span> {macroSummary}</div>}
                      {program?.training_days != null && <div><span className="coach-muted">Training days:</span> {program.training_days}</div>}
                      {program?.training_phase && <div><span className="coach-muted">Phase:</span> {program.training_phase}</div>}
                    </div>
                  )}

                  {editProgram && (
                    <div className="coach-form">
                      <div className="coach-grid-2">
                        <div className="coach-row">
                          <label className="coach-label">Calories</label>
                          <input className="coach-input" value={programForm.calories} onChange={(e) => setProgramForm((p) => ({ ...p, calories: e.target.value }))} disabled={busy} />
                        </div>
                        <div className="coach-row">
                          <label className="coach-label">Training days</label>
                          <input className="coach-input" value={programForm.training_days} onChange={(e) => setProgramForm((p) => ({ ...p, training_days: e.target.value }))} disabled={busy} />
                        </div>
                        <div className="coach-row">
                          <label className="coach-label">Protein (g)</label>
                          <input className="coach-input" value={programForm.protein_g} onChange={(e) => setProgramForm((p) => ({ ...p, protein_g: e.target.value }))} disabled={busy} />
                        </div>
                        <div className="coach-row">
                          <label className="coach-label">Carbs (g)</label>
                          <input className="coach-input" value={programForm.carbs_g} onChange={(e) => setProgramForm((p) => ({ ...p, carbs_g: e.target.value }))} disabled={busy} />
                        </div>
                        <div className="coach-row">
                          <label className="coach-label">Fats (g)</label>
                          <input className="coach-input" value={programForm.fats_g} onChange={(e) => setProgramForm((p) => ({ ...p, fats_g: e.target.value }))} disabled={busy} />
                        </div>
                        <div className="coach-row">
                          <label className="coach-label">Phase</label>
                          <input className="coach-input" value={programForm.training_phase} onChange={(e) => setProgramForm((p) => ({ ...p, training_phase: e.target.value }))} disabled={busy} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="coach-subcard" style={{ marginTop: 12 }}>
                    <div className="coach-subcard-title">Training Preferences</div>
                    <div className="coach-kv" style={{ marginTop: 8 }}>
                      <div><span className="coach-muted">Training days:</span> {workoutPreferencesSummary.trainingDaysLabel}</div>
                      <div><span className="coach-muted">Primary goal:</span> {workoutPreferencesSummary.primaryGoalLabel}</div>
                      <div><span className="coach-muted">Preferred split:</span> {workoutPreferencesSummary.splitLabel}</div>
                      <div><span className="coach-muted">Preferred weekdays:</span> {workoutPreferencesSummary.preferredDaysLabel}</div>
                      <div><span className="coach-muted">Focus areas:</span> {workoutPreferencesSummary.focusAreasLabel}</div>
                      <div><span className="coach-muted">Experience level:</span> {workoutPreferencesSummary.experienceLabel}</div>
                      <div><span className="coach-muted">Limitations:</span> {workoutPreferencesSummary.limitationsLabel}</div>
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Workout Plan</h2>
                {!editWorkouts && (
                  <button type="button" className="coach-btn secondary" onClick={startEditWorkouts} disabled={busy || workoutDays.length === 0}>
                    Edit
                  </button>
                )}
                {editWorkouts && (
                  <div className="coach-actions" style={{ marginTop: 0 }}>
                    <button type="button" className="coach-btn" onClick={saveWorkouts} disabled={busy}>Save</button>
                    <button type="button" className="coach-btn secondary" onClick={cancelEditWorkouts} disabled={busy}>Cancel</button>
                    {savingWorkouts && (
                      <div className="coach-save-status" role="status" aria-live="polite">
                        <LoadingAnimation compact />
                        <span>Saving...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="coach-card-body">
                {workoutDays.length === 0 && <div className="coach-alert">No workout days found yet.</div>}
                {workoutDays.map((day) => {
                  const displayName = day.display_name || day.day_name || 'Day';
                  const displayFocus = day.display_focus || day.focus || '';
                  const displayDuration = day.display_duration_min ?? day.duration_min;
                  const dayDraft = workoutDayDraft[day.id] || {};
                  const dayNameValue = dayDraft.display_name ?? '';
                  const dayNameIsPreset = WORKOUT_DAY_NAME_OPTIONS.includes(dayNameValue);
                  const dayNameMode = dayDraft.display_name_mode || (dayNameValue && !dayNameIsPreset ? 'custom' : 'preset');
                  const dayNameSelectValue = dayNameMode === 'custom'
                    ? '__custom__'
                    : (dayNameIsPreset ? dayNameValue : '');
                  const fallbackPreset = WORKOUT_DAY_NAME_OPTIONS.includes(day.day_name)
                    ? day.day_name
                    : (WORKOUT_DAY_NAME_OPTIONS[0] || '');
                  const dayFocusValue = dayDraft.display_focus ?? '';
                  const dayDurationValue = dayDraft.display_duration_min ?? '';
                  const dayTypeValue = dayDraft.display_type ?? inferWorkoutDisplayType(day.day_name);
                  const dayNotesValue = dayDraft.notes ?? '';
                  const dayNotes = (day.raw_payload && typeof day.raw_payload === 'object' ? day.raw_payload.notes : '') || '';
                  const list = editWorkouts
                    ? Object.values(workoutDraft)
                      .filter((r) => r.day_id === day.id && !r.__deleted)
                      .sort((a, b) => (a.ord || 0) - (b.ord || 0))
                    : (exercisesByDayId.get(day.id) || []);
                  return (
                    <div key={day.id} className="coach-subcard">
                      <div className={`coach-subcard-header ${editWorkouts ? 'editing' : ''}`}>
                        {editWorkouts ? (
                          <div className="coach-subcard-edit">
                            <div className="coach-subcard-edit-actions">
                              <button type="button" className="coach-btn secondary" onClick={() => addExerciseDraft(day.id)} disabled={busy}>
                                Add Exercise
                              </button>
                            </div>
                            <div className="coach-subcard-edit-grid">
                              <div className="coach-row coach-title-row">
                                <label className="coach-label">Title</label>
                                {dayNameSelectValue === '__custom__' ? (
                                  <div className="coach-title-custom">
                                    <input
                                      className="coach-input slim"
                                      placeholder="Custom title"
                                      value={dayNameValue}
                                      onChange={(e) => patchWorkoutDayDraft(day.id, { display_name: e.target.value, display_name_mode: 'custom' })}
                                      disabled={busy}
                                    />
                                    <button
                                      type="button"
                                      className="coach-btn compact"
                                      onClick={() => patchWorkoutDayDraft(day.id, { display_name: fallbackPreset, display_name_mode: 'preset' })}
                                      disabled={busy}
                                    >
                                      Use preset
                                    </button>
                                  </div>
                                ) : (
                                  <div className="coach-select-wrap">
                                    <select
                                      className="coach-select-input"
                                      value={dayNameSelectValue || fallbackPreset}
                                      onChange={(e) => {
                                        const next = e.target.value;
                                        if (next === '__custom__') {
                                          patchWorkoutDayDraft(day.id, { display_name: '', display_name_mode: 'custom' });
                                        } else {
                                          patchWorkoutDayDraft(day.id, { display_name: next, display_name_mode: 'preset' });
                                        }
                                      }}
                                      disabled={busy}
                                    >
                                      {WORKOUT_DAY_NAME_OPTIONS.map((opt) => (
                                        <option key={`${day.id}-${opt}`} value={opt}>{opt}</option>
                                      ))}
                                      <option value="__custom__">Custom...</option>
                                    </select>
                                  </div>
                                )}
                              </div>
                              <div className="coach-row">
                                <label className="coach-label">Subtext</label>
                                <input
                                  className="coach-input slim"
                                  value={dayFocusValue}
                                  onChange={(e) => patchWorkoutDayDraft(day.id, { display_focus: e.target.value })}
                                  disabled={busy}
                                />
                              </div>
                              <div className="coach-row">
                                <label className="coach-label">Time (min)</label>
                                <input
                                  className="coach-input slim"
                                  value={dayDurationValue}
                                  onChange={(e) => patchWorkoutDayDraft(day.id, { display_duration_min: e.target.value })}
                                  disabled={busy}
                                  inputMode="numeric"
                                />
                              </div>
                              <div className="coach-row">
                                <label className="coach-label">Type</label>
                                <div className="coach-select-wrap">
                                  <select
                                    className="coach-select-input"
                                    value={dayTypeValue || 'strength'}
                                    onChange={(e) => patchWorkoutDayDraft(day.id, { display_type: e.target.value })}
                                    disabled={busy}
                                  >
                                    {WORKOUT_DAY_TYPES.map((opt) => (
                                      <option key={`${day.id}-${opt.value}`} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="coach-subcard-title">{displayName}</div>
                            <div className="coach-muted">{displayFocus} {displayDuration ? `• ${displayDuration} min` : ''}</div>
                          </div>
                        )}
                      </div>
                      <div className="coach-table">
                        {editWorkouts ? (
                          <div className="coach-table-row coach-table-head workout-exercise-head">
                            <div>Name</div><div>Find exercise</div><div>Sets</div><div>Rep min</div><div>Rep max</div><div>Rest (sec)</div><div />
                          </div>
                        ) : (
                          <div className="coach-table-row coach-table-head">
                            <div>Name</div><div>Sets</div><div>Rep min</div><div>Rep max</div><div>Rest (sec)</div><div />
                          </div>
                        )}
                        {list.map((ex) => (
                          <div key={ex.id} className={`coach-table-row ${editWorkouts ? 'workout-exercise-row' : ''}`}>
                            {(() => {
                              const needsSelection = editWorkouts && ex.__new && !ex.name;
                              return (
                                <>
                                  <input
                                    className="coach-input slim"
                                    value={ex.name || ''}
                                    placeholder={needsSelection ? 'Select an exercise' : 'New exercise'}
                                    onChange={(e) => patchWorkoutDraft(ex.id, { name: e.target.value })}
                                    disabled={!editWorkouts || busy || needsSelection}
                                    style={needsSelection ? { cursor: 'not-allowed' } : undefined}
                                    readOnly={!editWorkouts}
                                  />
                                  {editWorkouts && (
                                    <div className="coach-exercise-find">
                                      <button
                                        type="button"
                                        className="coach-btn secondary compact"
                                        onClick={() => openExercisePicker(ex.id)}
                                        disabled={busy}
                                      >
                                        Select
                                      </button>
                                    </div>
                                  )}
                                  <input
                                    className="coach-input slim"
                                    value={ex.sets ?? ''}
                                    onChange={(e) => patchWorkoutDraft(ex.id, { sets: e.target.value })}
                                    disabled={!editWorkouts || busy || needsSelection}
                                    style={needsSelection ? { cursor: 'not-allowed' } : undefined}
                                    readOnly={!editWorkouts}
                                  />
                                  <input
                                    className="coach-input slim"
                                    value={ex.rep_min ?? ''}
                                    onChange={(e) => patchWorkoutDraft(ex.id, { rep_min: e.target.value })}
                                    disabled={!editWorkouts || busy || needsSelection}
                                    style={needsSelection ? { cursor: 'not-allowed' } : undefined}
                                    readOnly={!editWorkouts}
                                  />
                                  <input
                                    className="coach-input slim"
                                    value={ex.rep_max ?? ''}
                                    onChange={(e) => patchWorkoutDraft(ex.id, { rep_max: e.target.value })}
                                    disabled={!editWorkouts || busy || needsSelection}
                                    style={needsSelection ? { cursor: 'not-allowed' } : undefined}
                                    readOnly={!editWorkouts}
                                  />
                                  <input
                                    className="coach-input slim"
                                    value={ex.rest_sec ?? ''}
                                    onChange={(e) => patchWorkoutDraft(ex.id, { rest_sec: e.target.value })}
                                    disabled={!editWorkouts || busy || needsSelection}
                                    style={needsSelection ? { cursor: 'not-allowed' } : undefined}
                                    readOnly={!editWorkouts}
                                  />
                                  {editWorkouts ? (
                                    <button type="button" className="coach-btn danger" onClick={() => deleteExerciseDraft(ex.id)} disabled={busy}>Delete</button>
                                  ) : (
                                    <div />
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ))}
                        {list.length === 0 && <div className="coach-muted" style={{ padding: '8px 0' }}>No exercises for this day.</div>}
                      </div>
                      <div className="coach-workout-day-notes">
                        <label className="coach-label">Workout notes</label>
                        <textarea
                          className="coach-input coach-workout-notes-input"
                          value={editWorkouts ? dayNotesValue : dayNotes}
                          onChange={editWorkouts ? (e) => patchWorkoutDayDraft(day.id, { notes: e.target.value }) : undefined}
                          readOnly={!editWorkouts}
                          rows={3}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Meal Plan</h2>
                {!editMealPlan && (
                  <button type="button" className="coach-btn secondary" onClick={startEditMealPlan} disabled={busy || !mealDay?.id}>
                    Edit
                  </button>
                )}
                {editMealPlan && (
                  <div className="coach-actions" style={{ marginTop: 0 }}>
                    <button type="button" className="coach-btn" onClick={saveMealPlan} disabled={busy}>Save</button>
                    <button type="button" className="coach-btn secondary" onClick={cancelEditMealPlan} disabled={busy}>Cancel</button>
                    {mealDay?.id && (
                      <button type="button" className="coach-btn secondary" onClick={addMealDraft} disabled={busy}>Add Meal</button>
                    )}
                    {savingMeals && (
                      <div className="coach-save-status" role="status" aria-live="polite">
                        <LoadingAnimation compact />
                        <span>Saving...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="coach-card-body">
                {!mealDay && <div className="coach-alert">No meal plan saved yet.</div>}
                {mealDay && (
                  <>
                    <div className="coach-muted">{mealDay.day_name || 'Daily'} {(mealDayTotals?.calories ?? mealDay.total_kcal) ? `• ${Math.round(mealDayTotals?.calories ?? mealDay.total_kcal)} kcal` : ''}</div>
                    <div className="coach-table" style={{ marginTop: 12 }}>
                      <div className="coach-table-row coach-table-head meal-plan">
                        <div className="coach-meal-num-head">#</div><div>Name</div><div>Time</div><div>Purpose</div><div />
                      </div>
                      {(editMealPlan ? Object.values(mealDraft).filter((r) => !r.__deleted).sort((a, b) => (toNumOrNull(a.meal_number) || 0) - (toNumOrNull(b.meal_number) || 0)) : meals).map((m) => (
                        <React.Fragment key={m.id}>
                          {(() => {
                            const isExpanded = true;
                            const num = Number(m.meal_number);
                            const payloadMeal = Number.isFinite(num) ? mealPayloadByNumber.get(num) : null;
                            const foods = Array.isArray(payloadMeal?.foods) ? payloadMeal.foods : [];
                            const draftMacros = m.macros && typeof m.macros === 'object' ? m.macros : null;
                            const payloadMacros = getPayloadMealMacros(payloadMeal);
                            const foodMacroTotals = sumFoodMacros(foods);
                            const macroSource = foodMacroTotals
                              || payloadMacros
                              || ((draftMacros && hasMacros(draftMacros)) ? draftMacros : null);
                            const calories = macroSource?.calories ?? macroSource?.kcal ?? macroSource?.total_kcal ?? macroSource?.totalCalories ?? null;
                            const protein = macroSource?.protein_g ?? macroSource?.protein ?? null;
                            const carbs = macroSource?.carbs_g ?? macroSource?.carbs ?? null;
                            const fats = macroSource?.fats_g ?? macroSource?.fats ?? null;
                            const derivedCalories = calories != null ? Math.round(Number(calories) || 0) : deriveCaloriesFromMacros(protein, carbs, fats);
                            const hasRowMacros = calories != null || protein != null || carbs != null || fats != null;
                            const viewMacroValues = {
                              calories: derivedCalories != null ? String(derivedCalories) : '',
                              protein_g: protein != null ? formatMacroValue(protein) : '',
                              carbs_g: carbs != null ? formatMacroValue(carbs) : '',
                              fats_g: fats != null ? formatMacroValue(fats) : '',
                            };
                            const macroInputs = viewMacroValues;
                            const catCounts = {};

                            return (
                              <div className={`coach-meal-card ${isExpanded ? 'expanded' : ''} ${editMealPlan ? 'editing' : ''}`}>
                                {editMealPlan ? (
                                  <div className="coach-table-row meal-plan coach-meal-header">
                                    <div className="coach-meal-num-cell">
                                      <input
                                        className="coach-input slim coach-meal-num-input"
                                        value={m.meal_number ?? ''}
                                        onChange={(e) => patchMealDraft(m.id, { meal_number: e.target.value })}
                                        disabled={busy}
                                        inputMode="numeric"
                                      />
                                    </div>
                                    <input
                                      className="coach-input slim"
                                      value={m.name || ''}
                                      onChange={(e) => patchMealDraft(m.id, { name: e.target.value })}
                                      disabled={!editMealPlan || busy}
                                      readOnly={!editMealPlan}
                                    />
                                    <input
                                      className="coach-input slim"
                                      value={m.time_window || ''}
                                      onChange={(e) => patchMealDraft(m.id, { time_window: e.target.value })}
                                      disabled={!editMealPlan || busy}
                                      readOnly={!editMealPlan}
                                    />
                                    <input
                                      className="coach-input slim"
                                      value={m.purpose || ''}
                                      onChange={(e) => patchMealDraft(m.id, { purpose: e.target.value })}
                                      disabled={!editMealPlan || busy}
                                      readOnly={!editMealPlan}
                                    />
                                    <button type="button" className="coach-btn danger" onClick={() => deleteMealDraft(m.id)} disabled={busy}>Delete</button>
                                  </div>
                                ) : (
                                  <div className="coach-table-row meal-plan coach-meal-header">
                                    <div className="coach-meal-num-cell">
                                      <div className="coach-meal-num-badge">{m.meal_number ?? '—'}</div>
                                    </div>
                                    <div className="coach-meal-cell coach-meal-name">{m.name || '—'}</div>
                                    <div className="coach-meal-cell">{m.time_window || '—'}</div>
                                    <div className="coach-meal-cell">{m.purpose || '—'}</div>
                                    <div />
                                  </div>
                                )}

                                {(editMealPlan || hasRowMacros) && (
                                  <div className="coach-meal-macro-editor">
                                    <div className="coach-meal-macro-field">
                                      <label className="coach-label">Calories</label>
                                      <input
                                        className="coach-input slim"
                                        value={macroInputs.calories}
                                        readOnly
                                        disabled
                                      />
                                    </div>
                                    <div className="coach-meal-macro-field">
                                      <label className="coach-label">Protein (g)</label>
                                      <input
                                        className="coach-input slim"
                                        value={macroInputs.protein_g}
                                        readOnly
                                        disabled
                                      />
                                    </div>
                                    <div className="coach-meal-macro-field">
                                      <label className="coach-label">Carbs (g)</label>
                                      <input
                                        className="coach-input slim"
                                        value={macroInputs.carbs_g}
                                        readOnly
                                        disabled
                                      />
                                    </div>
                                    <div className="coach-meal-macro-field">
                                      <label className="coach-label">Fats (g)</label>
                                      <input
                                        className="coach-input slim"
                                        value={macroInputs.fats_g}
                                        readOnly
                                        disabled
                                      />
                                    </div>
                                    {editMealPlan && (
                                      <div
                                        className="coach-muted"
                                        style={{ marginTop: 6, fontSize: 12, display: 'flex', alignItems: 'flex-end' }}
                                      >
                                        Calculated from ingredient macros for this meal.
                                      </div>
                                    )}
                                    <div className="coach-meal-notes">
                                      <label className="coach-label">Notes</label>
                                      <textarea
                                        className="coach-input coach-meal-notes-input"
                                        value={m.notes || ''}
                                        onChange={editMealPlan ? (e) => patchMealDraft(m.id, { notes: e.target.value }) : undefined}
                                        readOnly={!editMealPlan}
                                        rows={3}
                                      />
                                    </div>
                                  </div>
                                )}

                                {isExpanded && (
                                  <div className="coach-meal-details">
                                    <div className="coach-meal-macros">
                                      {!hasRowMacros && <span className="coach-muted">No macros found for this meal.</span>}
                                      {hasRowMacros && (
                                        <>
                                          {derivedCalories != null && <span className="meal-kcal-pill">{derivedCalories} kcal</span>}
                                          {(protein != null || carbs != null || fats != null) && (
                                            <span className="meal-macro-summary">
                                              {protein != null && <span className="meal-macro-pill macro-protein">{Math.round(Number(protein) || 0)}g P</span>}
                                              {carbs != null && <span className="meal-macro-pill macro-carbs">{Math.round(Number(carbs) || 0)}g C</span>}
                                              {fats != null && <span className="meal-macro-pill macro-fats">{Math.round(Number(fats) || 0)}g F</span>}
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
		                                    <div className="coach-food-list">
		                                      {foods.length === 0 && <div className="coach-muted">No food items found for this meal.</div>}
		                                      {foods.map((food, idx) => {
		                                        const category = String(food?.category || 'other').toLowerCase();
		                                        const categoryIndex = catCounts[category] || 0;
                                        catCounts[category] = categoryIndex + 1;
                                        const name = food?.item || food?.name || food?.food || food?.label || 'Food';
                                        const amountValue = food?.qty || food?.amount || food?.portion || food?.serving || food?.nutrition_amount || '';
                                        const unit = food?.unit || food?.uom || '';
                                        const amountText = amountValue ? `${amountValue}${unit ? ` ${unit}` : ''}` : '';
                                        const foodMacros = computeFoodMacros(food);
                                        const macroLine = formatMacroLine(foodMacros);
                                        const metaParts = [
                                          formatFoodCategoryLabel(category),
                                          amountText || null,
                                          macroLine || null,
                                        ].filter(Boolean);
                                        return (
                                          <div key={`${name}-${idx}`} className="coach-food-row">
                                            <div className="coach-food-left">
                                              <div className="coach-food-name">{name}</div>
                                              {metaParts.length ? <div className="coach-food-meta">{metaParts.join(' | ')}</div> : null}
                                            </div>
                                            {editMealPlan && (
                                              <div className="coach-food-actions">
                                                <button
                                                  type="button"
                                                  className="coach-btn secondary"
                                                  onClick={() => openSwapModal({
                                                    mealNumber: num,
                                                    category,
                                                    categoryIndex,
                                                    currentLabel: name,
                                                    currentAmount: amountText,
                                                    currentMacros: foodMacros,
                                                  })}
                                                  disabled={busy || !Number.isFinite(num)}
                                                >
                                                  Swap
                                                </button>
                                                <button
                                                  type="button"
                                                  className="coach-btn danger"
                                                  onClick={() => removeIngredient({
                                                    mealNumber: num,
                                                    foodIndex: idx,
                                                    category,
                                                    categoryIndex,
                                                    itemName: name,
                                                  })}
                                                  disabled={busy || !Number.isFinite(num)}
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            )}
		                                          </div>
		                                        );
		                                      })}
		                                      {editMealPlan && (
		                                        <div className="coach-food-row" style={{ justifyContent: 'flex-start' }}>
                                          <button
                                            type="button"
                                            className="coach-btn secondary"
                                            onClick={() => openSwapModal({
                                              mealNumber: num,
                                              mode: 'add',
                                            })}
                                            disabled={busy || !Number.isFinite(num)}
                                          >
                                            Add ingredient
                                          </button>
		                                        </div>
		                                      )}
		                                    </div>
		                                  </div>
		                                )}
                              </div>
                            );
                          })()}
                        </React.Fragment>
                      ))}
                      {(editMealPlan ? Object.values(mealDraft).filter((r) => !r.__deleted).length : meals.length) === 0 && (
                        <div className="coach-muted" style={{ padding: '8px 0' }}>No meals for this plan.</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Recent Check-ins</h2>
              </div>
              <div className="coach-card-body">
                {weeklyCheckins.length === 0 && <div className="coach-alert">No weekly check-ins yet.</div>}
                {weeklyCheckins.length > 0 && (
                  <div className="coach-table">
                    <div className="coach-table-row coach-table-head">
                      <div>Week</div><div>Weight</div><div>Energy</div><div>Sleep</div><div>Workouts</div><div>Images</div>
                    </div>
                    {weeklyCheckins.map((w) => {
                      const imageRefs = w.imageRefs || [];
                      const imageUrls = w.imageUrls || imageRefs.map((ref) => ref.url).filter(Boolean);
                      const imageLabel = imageRefs.length ? `${imageRefs.length} photo${imageRefs.length > 1 ? 's' : ''}` : '-';
                      return (
                        <React.Fragment key={w.id}>
                          <div className="coach-table-row">
                            <div className="coach-muted">{w.week_start || '-'}</div>
                            <div>{w.weight ?? '-'}</div>
                            <div>{w.energy_rating ?? '-'}</div>
                            <div>{w.sleep ?? '-'}</div>
                            <div>{w.workouts_completed ?? '-'}</div>
                            <div>{imageLabel}</div>
                          </div>
                          {imageRefs.length > 0 && imageUrls.length === 0 && (
                            <div className="coach-table-row full-row">
                              <div className="coach-muted">Photos are unavailable or still loading.</div>
                            </div>
                          )}
                          {imageUrls.length > 0 && (
                            <div className="coach-table-row full-row">
                              <div className="coach-image-grid">
                                {imageUrls.map((src, idx) => (
                                  <img
                                    key={`${w.id}-image-${idx}`}
                                    src={src}
                                    alt={`Weekly check-in ${w.week_start || 'photo'} ${idx + 1}`}
                                    className="coach-image-thumb"
                                    loading="lazy"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Daily Logs</h2>
              </div>
              <div className="coach-card-body">
                {dailyLogs.length === 0 && <div className="coach-alert">No daily logs yet.</div>}
                {dailyLogs.length > 0 && (
                  <div className="coach-table">
                    <div className="coach-table-row coach-table-head">
                      <div>Date</div><div>Weight</div><div>Steps</div><div>Calories</div>
                    </div>
                    {dailyLogs.map((d) => (
                      <div className="coach-table-row" key={d.id}>
                        <div className="coach-muted">{d.logdate || '-'}</div>
                        <div>{d.weight ?? '-'}</div>
                        <div>{d.steps ?? '-'}</div>
                        <div>{d.calories ?? '-'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="coach-card">
              <div className="coach-card-header">
                <h2 className="coach-card-title">Recent Workout Sets</h2>
              </div>
              <div className="coach-card-body">
                {workoutLogs.length === 0 && <div className="coach-alert">No workout logs yet.</div>}
                {workoutLogs.length > 0 && (
                  <div className="coach-table">
                    <div className="coach-table-row coach-table-head">
                      <div>Week</div><div>Day</div><div>Exercise</div><div>Set</div><div>Weight</div><div>Reps</div>
                    </div>
                    {workoutLogs.map((l) => (
                      <div className="coach-table-row" key={l.id}>
                        <div className="coach-muted">{l.week_key || '-'}</div>
                        <div>{l.day_name || '-'}</div>
                        <div>{l.exercise_name || '-'}</div>
                        <div className="coach-muted">{(l.set_index ?? 0) + 1}</div>
                        <div>{l.weight ?? '-'}</div>
                        <div>{l.reps ?? '-'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
